import { useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { useAsyncAction } from "@/hooks/use-async-action";
import { useAutoHideScroll } from "@/hooks/use-auto-hide-scroll";
import { Button } from "@/components/ui/button";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { ArrowRight, BotMessageSquare, ChevronLeft, ChevronRight, Info, KeyRound, Lightbulb, Plus, Star } from "lucide-react";
import { GetQuestionListResponse, QuestionModel, StudentMessage, UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { Input } from "./ui/input";
import { ImageUpload } from "./ui/image-upload";
import { getAnswerTips, recordHintStep, ocrQuestion, remarkAnswer, setBookmark, setStudentMessage as setStudentMessageRequest, submitAnswer, submitAnswerText, submitDraft, getAssignmentStat, getStudentMessage, answerStart, answerEnd, setSelfAssessmentResult, getQuestionList } from "@/services/generatedAPIs";
import { useGamification } from "@/contexts/GamificationContext";
import { flyKpToHeader, flyGsToHeader } from "@/components/gamification/gamificationEffects";
import { useTranslation } from "react-i18next";
import { i18n as i18nType } from "i18next";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router";
import { ROUTE } from "@/routes/constants";
import './css/MathField.css';
import { ReactSketchCanvasRef } from 'react-sketch-canvas';
import { AnswerCanvas } from "./answer-canvas";
import StudentMessageDialog from "./student-message-dialog";
import { toast } from "sonner";
import { LocalImage, processImage, RichText, BackendQuestionFileJsonKind, BackendQuestionFileJson, removeDisplaylines, processHandWriting, wrapDisplaylines, parseImagesFromFilesJson } from "@/utils/preprocess";
import { compareDesc } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";

import { MathfieldElement } from "mathlive";
import { ReadonlyQuestionDisplay, installMathFieldWrapper } from "./readonly-question-display";
import { AiReviewDisplay } from "./ai-review-display";
import { getQuestionText, getStandardAnswerText, HOMEWORK_STATE } from "@/lib/utils";
import { generateAnswerSessionToken } from "@/lib/answerSession";
import { HomeworkPreviewDialog, PreviewMode } from "./homework-preview-dialog";
import { Slider } from "@/components/ui/slider";
import { LoadingOverlay } from "@/components/ui/loading-overlay";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import { Checkbox } from "./ui/checkbox";
import { parseMultipleChoiceOptions } from "@/utils/mcq-utils";

import { OnResizeCallback } from 'react-resize-detector';

// Declare the math-field element type
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'math-field': any;
    }
  }
}

MathfieldElement.soundsDirectory = null;
MathfieldElement.keypressVibration = false;
MathfieldElement.fontsDirectory = null;

/**
 * 修复触屏点击 math-field「≡」菜单(切换 Math/Text/LaTeX)无反应。
 * 根因:MathLive 在 toggle 的 pointerdown 即 menu.show() 并挂全屏 Scrim;触屏 tap 后合成的 click
 * 命中该 Scrim,被 Scrim.handleEvent 判为点遮罩 → 立即关菜单(鼠标因 click 派发到共同祖先不受影响)。
 * 处理:仅吞掉开菜单后 600ms 内、composedPath 含 scrim 的那一下尾随 click;仅 touch 生效,全局装一次。
 */
let __mathfieldTouchMenuFixInstalled = false;
function installMathfieldTouchMenuFix() {
  if (__mathfieldTouchMenuFixInstalled || typeof document === "undefined") return;
  __mathfieldTouchMenuFixInstalled = true;

  let lastToggleTouchTs = 0;

  const pathHasMenuToggle = (path: EventTarget[]) =>
    path.some(
      (el) => el instanceof Element && el.getAttribute("part") === "menu-toggle",
    );
  const pathHasScrim = (path: EventTarget[]) =>
    path.some(
      (el) =>
        el instanceof HTMLElement &&
        el.getAttribute("role") === "presentation" &&
        getComputedStyle(el).position === "fixed",
    );

  document.addEventListener(
    "pointerdown",
    (ev) => {
      if (ev.pointerType === "touch" && pathHasMenuToggle(ev.composedPath())) {
        lastToggleTouchTs = ev.timeStamp;
      }
    },
    true,
  );

  document.addEventListener(
    "click",
    (ev) => {
      if (lastToggleTouchTs === 0) return;
      const dt = ev.timeStamp - lastToggleTouchTs;
      if (dt < 0 || dt > 600) return;
      if (pathHasScrim(ev.composedPath())) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
        lastToggleTouchTs = 0; // 只吞「开菜单」这一下的尾随 click
      }
    },
    true,
  );
}

const BUBBLE_TOOLTIP_CLASS =
  "rounded-md px-3 py-1.5 text-xs font-bold shadow-md bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)]";
const BUBBLE_TOOLTIP_ARROW_CLASS =
  "bg-[var(--practice-dialog-accent)] fill-[var(--practice-dialog-accent)]";

enum AnswerState {
  WIP,
  CORRECT,
  WRONG,
  DRAFT,
  PENDING_REVIEW, // 等候教师批改
}

const heightEvents: string[] = ['select', 'slideFocus']

let globalId = 0;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export interface QuizItemHandle {
  onSubmit: (skipRewardAnimation?: boolean) => Promise<{ currencyType?: string; amount?: number; eventType?: string; description?: string }[] | undefined>;
  getLocalImages: () => LocalImage[];
}

function QuizItem({ readonly, dueDate, questionModel, onStateChanged, mode, isLastQuestion, assignmentId, currentIndex, totalItems, onNavigateNext, onSubmitHomework, language, fill, assignmentTitle }: { readonly: boolean, dueDate?: Date, questionModel: QuestionModel, onStateChanged: (state: AnswerState) => void, mode?: number, isLastQuestion?: boolean, assignmentId?: number, currentIndex?: number, totalItems?: number, onNavigateNext?: () => void, onSubmitHomework?: () => void, language?: string, fill?: boolean, assignmentTitle?: string }, ref: React.ForwardedRef<QuizItemHandle>) {
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addKp, addGs, kp, gs, refreshPoints } = useGamification();
  const homeworkState = useLocation().state;
  // 答题区局部滚动条:仅滚动时/悬停时淡入(.is-scrolling 由钩子加/700ms 后撤),配 .ml-thin-scroll 细长隐藏样式。仅 fill 时该 div 有 overflow-y-auto,非 fill(contents)不触发滚动。
  const answerScrollRef = useAutoHideScroll<HTMLDivElement>();

  // Handle point awards from SubmitAnswerResponse
  const handlePointAwards = useCallback((pointAwards: { currencyType?: string; amount?: number; eventType?: string; description?: string }[] | undefined, sourceEl?: HTMLElement) => {
    if (!pointAwards || pointAwards.length === 0) return;
    const fromEl = sourceEl || document.body;
    let totalKpAwarded = 0;
    let totalGsAwarded = 0;
    for (const award of pointAwards) {
      if (award.currencyType === 'KP' && award.amount) {
        totalKpAwarded += award.amount;
      } else if (award.currencyType === 'GS' && award.amount) {
        totalGsAwarded += award.amount;
      }
    }
    // Capture current totals before any update, then update context after animation completes
    const hasBoth = totalKpAwarded > 0 && totalGsAwarded > 0;
    const spread = hasBoth ? 20 + Math.random() * 20 : 0;
    if (totalKpAwarded > 0) {
      const kpDelta = totalKpAwarded;
      void flyKpToHeader({ fromEl, delta: kpDelta, nextValue: kp + kpDelta, combined: hasBoth, xOffset: -spread }).then(() => { addKp(kpDelta); });
    }
    if (totalGsAwarded > 0) {
      const gsDelta = totalGsAwarded;
      void flyGsToHeader({ fromEl, delta: gsDelta, nextValue: gs + gsDelta, combined: hasBoth, xOffset: spread }).then(() => { addGs(gsDelta); });
    }
  }, [addKp, addGs, kp, gs]);

  let [questionBody] = useState<RichText>(processImage(t, true, BackendQuestionFileJsonKind.QuestionImage, questionModel.questionInfo.filesJson, getQuestionText(questionModel.questionInfo, i18n, language)));
  let [answerBody, setAnswerBody] = useState<RichText>(processImage(t, readonly, BackendQuestionFileJsonKind.AnswerImage, questionModel.question?.filesJson, questionModel.question?.answer));
  let [uploadFiles, setUploadFiles] = useState<File[]>([]);
  let [llmRawTexts, setLlmRawTexts] = useState<string>("");
  const [id, setId] = useState(++globalId);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [isSubmitted, setIsSubmitted] = useState(questionModel.question?.isCompleted ?? false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(questionModel.question?.isCorrectAnswer ?? false);
  const [isMethodologyCorrect, setIsMethodologyCorrect] = useState(questionModel.question?.isCorrectAnswer ?? false);
  const [showTips, setShowTips] = useState(false);
  const [expandedHints, setExpandedHints] = useState<Set<number>>(new Set());
  const [subQuestionResults, setSubQuestionResults] = useState<RemarkAnswerResult[]>([]);
  const [readmoreEndIndex, setReadmoreEndIndex] = useState(0);
  const [hintPage, setHintPage] = useState(0); // 手机横屏提示弹窗:当前提示页(0基)
  const [tipsApi, setTipsApi] = useState<CarouselApi | null>(null); // 桌面/平板提示轮播 embla 句柄(仅做滑动过渡)
  const [aiTips, setAITips] = useState<string[]>([]);
  const [aiUsageId, setAiUsageId] = useState<number | undefined>();
  const [tips, setTips] = useState<string>();
  const childRef = useRef<ReactSketchCanvasRef>(null);
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const submittingRef = useRef(false);
  const [inputKey, setInputKey] = useState(0);
  const [studentMessage, setStudentMessage] = useState<StudentMessage>();
  const [questionLocalImages, setQuestionLocalImages] = useState<LocalImage[]>([]);
  const answerRef = useRef(null);
  const [handWriting] = useState(processHandWriting(questionModel.question?.metaAnswer));
  const [handWritingStyle, setHandWritingStyle] = useState({ width: 100, height: 100 });
  const [currentInputMethod, setCurrentInputMethod] = useState("keyboard");
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [showAnswerConfirmOpen, setShowAnswerConfirmOpen] = useState(false);
  const [dontShowAnswerWarning, setDontShowAnswerWarning] = useState(false);

  const [teacherIsCorrect, setTeacherIsCorrect] = useState<boolean>(questionModel.question?.isCorrectAnswer || false);
  const [teacherComment, setTeacherComment] = useState<string>(questionModel.question?.teacherReview || "");
  const [isGradingSubmitting, setIsGradingSubmitting] = useState<boolean>(false);

  // MCQ display status.
  const isMultipleChoice = questionModel.questionInfo.questionType === "MCQ";
  const mcqData = isMultipleChoice ? parseMultipleChoiceOptions(questionBody.text) : null;
  let [mcqAnswers, setMCQAnswers] = useState<string[]>((isMultipleChoice && mcqData) ? questionModel.question?.answer?.split('') ?? [] : []);

  const [sessionId] = useState(() => Date.now().toString());

  // 追踪当前答案状态 - 根据初始状态初始化
  const getInitialAnswerState = (): AnswerState => {
    if (questionModel.question?.isCorrectAnswer) {
      return AnswerState.CORRECT;
    } else if (questionModel.question?.isCompleted && !questionModel.question?.isCorrectAnswer) {
      return AnswerState.WRONG;
    }
    return AnswerState.WIP;
  };
  const [currentAnswerState, setCurrentAnswerState] = useState<AnswerState>(getInitialAnswerState());
  const isAnsweredCorrectly = currentAnswerState === AnswerState.CORRECT;

  const handleFile = (file: Blob) => {
    const formData = new FormData();
    formData.append('image', file);
    execute(async () => {
      try {
        const res = await ocrQuestion(formData as any, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (res.code === 200 && res.data) {
          console.log("OCR question successful", res.data);
          const ocrRsp = JSON.parse(res.data);
          if (ocrRsp.status) {
            answerBody = {
              text: ocrRsp.res.info,
              backendImageFile: answerBody.backendImageFile,
              localImages: answerBody.localImages,
            };
            setAnswerBody(answerBody);
            setCurrentInputMethod("keyboard");

            const base64 = await blobToBase64(file);
            questionModel.question!.metaAnswer = JSON.stringify({ 'handwriting': base64 });
          } else {
            questionModel.question!.metaAnswer = undefined;
          }
        }
        else {
          console.error("OCR question failed", res.code, res.message);
          questionModel.question!.metaAnswer = undefined;
          toast.error(t('common.apierror') + res.message);
        }
      } catch (error) {
        console.error("OCR question error", error);
      } finally {
        setInputKey(inputKey + 1);
      }
    });
  };



  // OCR识别按钮处理函数
  const handleOcrRecognize = async () => {
    if (!childRef.current) return;

    try {
      // 检查是否有绘制内容
      const sketchingTime = await childRef.current.getSketchingTime();
      if (sketchingTime === 0) {
        toast.error(t('component.noHandwritingContent', { defaultValue: '没有手写内容' }));
        return;
      }

      setIsOcrProcessing(true);

      // 导出手写板内容为图片
      const dataUrl = await childRef.current.exportImage("png");
      const blob = await (await fetch(dataUrl)).blob();

      // 调用OCR识别
      await handleFile(blob);
    } catch (error) {
      console.error("OCR recognize error", error);
      toast.error(t('common.error'));
    } finally {
      setIsOcrProcessing(false);
    }
  };

  const handleImageUploadChange = (files: File[], allImages: any[]) => {
    const localImages: LocalImage[] = allImages.map((img) => ({
      name: img.name, // 保持 ImageUpload 组件生成的名称（如：1.jpg, 2.png...）
      blob: img.file,
      type: img.file.type,
    }))

    const newAnswerBody = {
      text: answerBody.text,
      backendImageFile: answerBody.backendImageFile,
      localImages: localImages,
    }

    setAnswerBody(newAnswerBody)
    answerBody = newAnswerBody
  }

  const onMathInput = (input: string) => {
    // Convert fraction format before setting
    input = removeDisplaylines(input);
    answerBody.text = input;
    setAnswerBody({
      text: input,
      backendImageFile: answerBody.backendImageFile,
      localImages: answerBody.localImages,
    });
  }

  const onAITips = (userId: number, questionId: number) => execute(async () => {
    const rsp = await getAnswerTips({
      chatId: userId + ":" + questionId + ":" + sessionId,
      question: getQuestionText(questionModel.questionInfo, i18n, language),
      standardAnswer: getStandardAnswerText(questionModel.questionInfo, i18n, language),
      answer: removeDisplaylines(answerBody.text!),
      questionId: questionModel.question?.id,
      assignmentId: assignmentId,
    })
    if (rsp.code === 200 && rsp.data) {
      console.log("AI tips successful", rsp.data.tips);
      const aiTips: string[] = rsp.data.tips.replaceAll("HINT_STEP_", t("common.info")).split("### END_STEP ###").filter((s: string) => s.length > 0).map((s: string) => s.trim().split("\n").slice(1).join("\n").trim());
      if (aiTips.length > 0) {
        setAITips(aiTips)
        setTips(aiTips[0]);
        setReadmoreEndIndex(1);
        setHintPage(0);
        setShowTips(true);
        setAiUsageId(rsp.data.aiUsageId);
        setExpandedHints(new Set([0]));
      }
    }
  });

  const onMCQRemark = useCallback(async (answerText: string): Promise<boolean | undefined> => {
    let standardAnswer = getStandardAnswerText(questionModel.questionInfo, i18n, language);
    if (!standardAnswer || standardAnswer.length === 0) {
      return;
    }

    standardAnswer = standardAnswer.replaceAll(' ', '').replaceAll(',', '').split('').sort().join('');
    const isCorrect = standardAnswer === answerText;
    const question = questionModel.question!;
    if (isCorrect) {
      setIsCorrect(true);
      setIsMethodologyCorrect(true);
      setTips("");
      setShowTips(false);
      question.isCorrectAnswer = true;
      setCurrentAnswerState(AnswerState.CORRECT);
      onStateChanged && onStateChanged(AnswerState.CORRECT);
    } else {
      setIsCorrect(false);
      question.isCorrectAnswer = false;
      setCurrentAnswerState(AnswerState.WRONG);
      onStateChanged && onStateChanged(AnswerState.WRONG);
    }
    return isCorrect;
  }, [questionModel, i18n, language, onStateChanged])

  const onAIRemark = useCallback((answerText: string, showTips: boolean): Promise<string | undefined> => execute(async () => {
    try {
      const rsp = await remarkAnswer({
        question: getQuestionText(questionModel.questionInfo, i18n, language),
        standardAnswer: getStandardAnswerText(questionModel.questionInfo, i18n, language),
        answer: answerText,
        questionId: questionModel.question?.id,
        showTips: showTips,
        chatId: getCurrentUser()!.id + ":" + questionModel.question?.id + ":" + sessionId,
      });
      console.log("AI remark successful", rsp.data);
      if (rsp.code === 200 && rsp.data) {
        const question = questionModel.question!;
        const results = rsp.data.results || [];
        setSubQuestionResults(results);
        if (results.length > 0) {
          const isCorrectArr = results.map(r => r.isCorrect ?? false);
          const isCorrect = isCorrectArr.every((value) => value);
          if (isCorrect) {
            setIsCorrect(isCorrect);
            setIsMethodologyCorrect(true); // 暂时不判断过程
            setTips("");
            setShowTips(false);
            question.isCorrectAnswer = true;
            question.errorPoints = "";
            setCurrentAnswerState(AnswerState.CORRECT);
            onStateChanged && onStateChanged(AnswerState.CORRECT);
          } else {
            setIsCorrect(false);
            console.log('josh showTips', showTips);
            if (showTips) {
              let tips = questionModel.questionInfo.standardAnswer ? t('common.answer') + ":" + questionModel.questionInfo.standardAnswer : "";
              tips += results.map((result, index) => {
                const isCorrect = result.isCorrect ?? false;
                const hintText = result.hintsIfNotCorrect || "";
                return '\n' + t('common.subquestion') + (index + 1) + ":" + (isCorrect ? t('component.correct') : t('component.wrong') + "\n" + hintText);
              }).join('\n');
              setTips(tips);
              setShowTips(true);
            }
            question.isCorrectAnswer = false;
            const errorTypesArr = results.map(r => r.errorType).filter(Boolean) as string[];
            const errorPointsSet = new Set<string>();
            errorTypesArr.flat().forEach((point) => { errorPointsSet.add(point.replaceAll(' ', '')); });
            question.errorPoints = Array.from(errorPointsSet).join(',');
            setCurrentAnswerState(AnswerState.WRONG);
            onStateChanged && onStateChanged(AnswerState.WRONG);
          }
        }
        return typeof rsp.data.rawText === 'string' ? rsp.data.rawText : "";
      }
    } catch (error) {
      console.error("AI remark error, falling back to teacher review", error);
      setIsCorrect(false);
      setCurrentAnswerState(AnswerState.WRONG);
      onStateChanged && onStateChanged(AnswerState.WRONG);
      return "";
    }
  }), [questionModel, i18n, language, t, onStateChanged, execute])

  // 老师批改提交函数
  const onTeacherGradingSubmit = async () => {
    if (getCurrentUser()?.role !== UserInfoResponseRole.TEACHER) {
      toast.error(t('common.unauthorized'));
      return;
    }

    setIsGradingSubmitting(true);

    try {
      // 更新问题对象
      const updatedQuestion = {
        ...questionModel.question!,
        isCorrectAnswer: teacherIsCorrect,
        teacherReview: teacherComment,
      };

      // 提交到后端
      const response = await submitAnswerText({
        model: JSON.stringify({ question: updatedQuestion, answerSessionId: generateAnswerSessionToken({ studentId: updatedQuestion.studentId, questionId: updatedQuestion.id }) })
      });

      if (response.code === 200 && response.data?.success !== false) {
        toast.success(t('component.gradingSuccess'));
        // 更新本地状态
        questionModel.question = updatedQuestion;
        // 触发状态更新
        const newState = updatedQuestion.isCorrectAnswer ? AnswerState.CORRECT : AnswerState.WRONG;
        setCurrentAnswerState(newState);
        onStateChanged(newState);
      } else {
        toast.error(t('common.apierror') + response.message);
      }
    } catch (error) {
      console.error('Error submitting grading:', error);
      toast.error(t('common.error'));
    } finally {
      setIsGradingSubmitting(false);
    }
  };

  const onSave = () => execute(async () => {
    if (childRef.current && (await childRef.current.getSketchingTime()) > 0 && !answerBody.text) {
      alert(t('component.needocr'));
      return;
    }

    const answerText = removeDisplaylines(answerBody.text);
    if (answerText.length === 0 && answerBody.localImages.length === 0) {
      alert(t('component.needanswer'));
      return;
    }

    let question = questionModel.question!;
    question.answer = answerText;
    question.isCompleted = false;
    question.draft = "1";

    try {
      const rsp = await submitDraft({ model: JSON.stringify({ question: question }), images: answerBody.localImages.map((value) => new File([value.blob], value.name, { type: value.type })) })
      if (rsp.code === 200 && rsp.data) {
        console.log("Save draft successful", rsp.data, question);
        toast.success(t('component.savesuccess'));

        // Fire answerEnd on save for last question (no navigation occurs, so index change won't fire it)
        if (isLastQuestion && questionModel.question?.id && !questionModel.question?.isCompleted) {
          console.log("[Response Time] Fire Answer End on save for last question", questionModel.question.id); //TODO: remove in production
          answerEnd({ questionId: questionModel.question.id }).catch(console.error);
        }

        setIsSubmitted(true);
        // Update state to DRAFT
        onStateChanged && onStateChanged(AnswerState.DRAFT);
        // Auto-navigate to next question in homework mode
        if (mode === 1 && onNavigateNext && !isLastQuestion) {
          setTimeout(() => {
            onNavigateNext();
          }, 500);
        }
      } else {
        console.error("Save draft failed", rsp.code, rsp.message);
        toast.error(t('common.apierror') + rsp.message);
      }
    } catch (error) {
      console.error("Save draft error", error);
      toast.error(t('common.error'));
    }
  });

  const onSubmit = useCallback((skipRewardAnimation?: boolean): Promise<{ currencyType?: string; amount?: number; eventType?: string; description?: string }[] | undefined> => execute(async () => {
    if (childRef.current && (await childRef.current.getSketchingTime()) > 0 && !answerBody.text) {
      alert(t('component.needocr'));
      return;
    }

    const answerText = removeDisplaylines(answerBody.text);
    if (answerText.length === 0 && answerBody.localImages.length === 0) {
      alert(t('component.needanswer'));
      return;
    }

    // Fire answerEnd for practice mode before submission
    if (mode === 2 && questionModel.question?.id && !questionModel.question?.isCompleted) {
      answerEnd({ questionId: questionModel.question.id }).catch(console.error);
    }

    try {
      if (answerBody.localImages.length > 0) {
        let question = questionModel.question!;
        question.answer = answerText;
        question.submitedAt = new Date().toISOString();
        question.isCompleted = question.answer ? true : false;
        question.isCorrectAnswer = false;
        question.aiReview = "";
        const rsp = await submitAnswer({ model: JSON.stringify({ question: question, answerSessionId: generateAnswerSessionToken({ studentId: question.studentId, questionId: question.id }) }), images: answerBody.localImages.map((value) => new File([value.blob], value.name, { type: value.type })) })
        if (rsp.code === 200 && rsp.data?.success !== false) {
          console.log("Submit answer successful", rsp.data, question);
          if (!skipRewardAnimation) {
            handlePointAwards(rsp.data?.pointAwards, submitBtnRef.current ?? undefined);
          }
          setStudentMessageRequest({
            studentMessage: {
              title: t('component.teacherreview'),
              description: "",
              replyType: 0,
              type: 1,
              teacherMessage: undefined,
              studentId: getCurrentUser()!.id,
              teacherId: questionModel.question!.teacherId,
              questionInfoId: questionModel.questionInfo.id,
              questionId: questionModel.question!.id,
            }
          }).then((res) => {
            if (res.code === 200) {
              console.log("setStudentMessage successful", res.data);
              console.log("Setting PENDING_REVIEW state");
              setTips(t('component.waitingreview'));
              setShowTips(true);
              setCurrentAnswerState(AnswerState.PENDING_REVIEW);
              setIsSubmitted(true);
              onStateChanged && onStateChanged(AnswerState.PENDING_REVIEW);
            } else {
              console.error("setStudentMessage failed", res.code, res.message);
            }
          }
          ).catch((error) => {
            console.error("setStudentMessage error", error);
          }
          ).finally(() => {
          });
          return rsp.data?.pointAwards;
        } else {
          console.error("Submit answer failed", rsp.code, rsp.message);
        }
      } else {
        let aiReviewText: string | undefined;
        let aiRemarkFailed = false;
        if (isMultipleChoice && mcqData) {
          const isCorrect = await onMCQRemark(answerText, i18n);
          if (typeof isCorrect === 'undefined') {
            aiReviewText = await onAIRemark(answerText, false);
            aiRemarkFailed = !aiReviewText;
          }
        } else {
          aiReviewText = await onAIRemark(answerText, false);
          aiRemarkFailed = !aiReviewText;
        }
        let question = questionModel.question!;
        question.answer = answerText;
        question.submitedAt = new Date().toISOString();
        question.isCompleted = question.answer ? true : false;
        if (aiRemarkFailed) {
          question.isCompleted = false;
          question.aiReview = "";
          question.isCorrectAnswer = false;
          question.errorPoints = "";
        } else {
          question.aiReview = aiReviewText;
        }
        const rsp = await submitAnswer({ model: JSON.stringify({ question: question, answerSessionId: generateAnswerSessionToken({ studentId: question.studentId, questionId: question.id }) }), images: answerBody.localImages.map((value) => new File([value.blob], value.name, { type: value.type })) })
        if (rsp.code === 200 && rsp.data?.success !== false) {
          console.log("Submit answer successful", rsp.data, question);
          if (!skipRewardAnimation) {
            handlePointAwards(rsp.data?.pointAwards, submitBtnRef.current ?? undefined);
          }
          if (aiRemarkFailed) {
            // AI remark failed — delegate to teacher review (same as image-answer path)
            setStudentMessageRequest({
              studentMessage: {
                title: t('component.teacherreview'),
                description: "",
                replyType: 0,
                type: 1,
                teacherMessage: undefined,
                studentId: getCurrentUser()!.id,
                teacherId: questionModel.question!.teacherId,
                questionInfoId: questionModel.questionInfo.id,
                questionId: questionModel.question!.id,
              }
            }).then((res) => {
              if (res.code === 200) {
                console.log("setStudentMessage successful (AI remark fallback)", res.data);
                setTips(t('component.waitingreview'));
                setShowTips(true);
                setCurrentAnswerState(AnswerState.PENDING_REVIEW);
                setIsSubmitted(true);
                onStateChanged && onStateChanged(AnswerState.PENDING_REVIEW);
              } else {
                console.error("setStudentMessage failed", res.code, res.message);
              }
            }).catch((error) => {
              console.error("setStudentMessage error", error);
            });
          } else {
            setIsSubmitted(true);
          }
          return rsp.data?.pointAwards;
        } else {
          console.error("Submit answer failed", rsp.code, rsp.message);
        }
      }
    } catch (error) {
      // Handle network or other errors
      console.error("Submit answer error", error);
    }
  }), [answerBody, mode, questionModel, handlePointAwards, onMCQRemark, onAIRemark, onStateChanged, t, i18n, getCurrentUser, isMultipleChoice, mcqData, execute])

  // 暴露 onSubmit 函数和 getLocalImages 函数供外部调用
  useImperativeHandle(ref, () => ({
    onSubmit,
    getLocalImages: () => answerBody.localImages
  }), [onSubmit, answerBody.localImages]);

  useEffect(() => {
    installMathFieldWrapper();
  });

  useEffect(() => {
    if (!readonly) {
      getStudentMessage({ questionId: questionModel.question!.id!, type: 0 })
        .then((rsp) => {
          if (rsp.code === 200 && rsp.data?.studentMessages.length) {
            setStudentMessage(rsp.data.studentMessages[0]);
          }
        })
        .catch((error) => { });
    }
  }, []);

  useEffect(() => {
    if (!readonly) {
      if (answerRef && answerRef.current) {
        // 触屏点 ≡ 菜单（切换输入模式）无反应的修复，全局只装一次
        installMathfieldTouchMenuFix();
        // Customize the mathfield when it is created
        answerRef.current.addEventListener(
          "keydown",
          (ev) => {
            if (ev.key === "\\") {
              ev.preventDefault();
              answerRef.current.executeCommand(["insert", "\\backslash"]);
            } else if (ev.key === "Enter") {
              ev.preventDefault();
            } else if (ev.key === "Escape") ev.preventDefault();
          },
          { capture: true },
        );
        if (answerRef.current.menuItems.length > 1) {
          answerRef.current.menuItems = answerRef.current.menuItems.filter(
            (item) => item.id === "mode",
          );
        }
        answerRef.current.mathModeSpace = '\\;';
        answerRef.current.letterShapeStyle = 'upright';
      }
    }
  }, []);

  useEffect(() => {
    // Process question images - 使用统一的图片解析函数
    const questionImages = parseImagesFromFilesJson(questionModel.questionInfo.filesJson, 'question');
    if (questionImages.length > 0) {
      questionImages.sort((a, b) => a.name > b.name ? 1 : 0);
      setQuestionLocalImages(questionImages);
    }

    // Process answer images - 使用统一的图片解析函数
    const answerImages = parseImagesFromFilesJson(questionModel.question?.filesJson, 'answer');
    if (answerImages.length > 0) {
      answerImages.sort((a, b) => a.name > b.name ? 1 : 0);

      // 更新 answerBody 以包含回答区图片
      setAnswerBody(prevAnswerBody => ({
        text: prevAnswerBody.text,
        backendImageFile: answerImages.map(img => ({ name: img.name, url: img.url, type: img.type })),
        localImages: answerImages,
      }));
    }

    // 做题模式下，检查是否有 PENDING_REVIEW 状态或已批改状态
    if (mode === 2 && !questionModel.question?.isCompleted) {
      getStudentMessage({ questionId: questionModel.question!.id, type: 1 })
        .then((rsp) => {
          if (rsp.code === 200 && rsp.data && rsp.data.studentMessages && rsp.data.studentMessages.length > 0) {
            const studentMessage = rsp.data.studentMessages[0];
            if (studentMessage.replyType === 0) {
              setCurrentAnswerState(AnswerState.PENDING_REVIEW);
            } else if (studentMessage.replyType === 1) {
              // 老师已批改，根据isCorrectAnswer判断状态
              if (questionModel.question?.isCorrectAnswer) {
                setCurrentAnswerState(AnswerState.CORRECT);
                setTips("");
                setShowTips(false);
              } else {
                setCurrentAnswerState(AnswerState.WRONG);
                // 显示老师的评论
                if (questionModel.question?.teacherReview) {
                  setTips(questionModel.question.teacherReview);
                  setShowTips(true);
                }
              }
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching student message:", error);
        });
    }
  }, []);

  const showTipsBelowDesc = (!isMultipleChoice || !mcqData) && !readonly && !isAnsweredCorrectly;
  const isAnswerFlow = aiTips.length === 0;
  const firstNewlineIdx = tips ? tips.indexOf('\n') : -1;
  const answerHeadLine = !tips ? '' : (firstNewlineIdx === -1 ? tips : tips.slice(0, firstNewlineIdx));
  const answerBodyLines = !tips || firstNewlineIdx === -1 ? '' : tips.slice(firstNewlineIdx + 1);
  // 提示分页(桌面/平板/手机横屏内联白卡共用):一次一条 + < > 翻页;翻到未消耗过的提示才计一次 recordHintStep
  const totalHints = aiTips.length;
  const goPrevHint = () => setHintPage((p) => Math.max(0, p - 1));
  const goNextHint = () => {
    const np = hintPage + 1;
    if (np >= totalHints) return;
    setHintPage(np);
    if (np + 1 > readmoreEndIndex) {
      if (aiUsageId) recordHintStep({ aiUsageId });
      setReadmoreEndIndex(np + 1);
    }
  };
  // 桌面/平板提示轮播:hintPage 变化 → 滑到对应条(带左右过渡动画);同时把容器高度同步为当前条
  // (embla 默认 flex 等高会按最长条 stretch 留白,故用 items-start + 手动量高,套主题目轮播同款 containerNode 高度范式)
  useEffect(() => {
    if (!tipsApi) return;
    if (tipsApi.selectedScrollSnap() !== hintPage) tipsApi.scrollTo(hintPage);
    const syncHeight = () => {
      const node = tipsApi.slideNodes()[tipsApi.selectedScrollSnap()];
      if (node) tipsApi.containerNode().style.height = `${node.offsetHeight}px`;
    };
    syncHeight();
    const reMeasure = setTimeout(syncHeight, 60); // 等 Markdown 渲染完再量一次
    tipsApi.on("select", syncHeight);
    tipsApi.on("reInit", syncHeight);
    return () => {
      clearTimeout(reMeasure);
      tipsApi.off("select", syncHeight);
      tipsApi.off("reInit", syncHeight);
    };
  }, [tipsApi, hintPage, aiTips]);
  const tipsBlock = showTips && tips ? (
    <div className="mt-3 [@media(max-height:500px)]:mt-1.5!">
      <div className="rounded-[25px] bg-white shadow-sm [@media(max-height:500px)]:rounded-none! [@media(max-height:500px)]:bg-transparent! [@media(max-height:500px)]:shadow-none!">
        <div className="px-6 py-5 2xl:px-[31px] 2xl:py-6 [@media(max-height:500px)]:px-2.5! [@media(max-height:500px)]:py-2!">
          {isAnswerFlow ? (
            <>
              {/* 答案行 — 24px / weight 450 (用户指定) */}
              <div className="text-[#006609] text-2xl leading-tight [@media(max-height:500px)]:text-sm!" style={{ fontWeight: 450 }}>
                <MarkdownRenderer>{answerHeadLine}</MarkdownRenderer>
              </div>
              {/* 子题对错详情 — 沿用 13px 正文样式 */}
              {answerBodyLines && (
                <div className="mt-3 text-[#006609] text-sm 2xl:text-[13px] leading-[1.55] [@media(max-height:500px)]:mt-1.5! [@media(max-height:500px)]:text-[11px]! [@media(max-height:500px)]:leading-[1.1]!">
                  <MarkdownRenderer>{answerBodyLines}</MarkdownRenderer>
                </div>
              )}
            </>
          ) : (
            <>
              {/* 提示流:标题「提示 N」(单条时不带序号) + 「查看更多提示」文字入口
                  (还有下一条提示时才显示;点击 = 下一条,与右下 > 同义,共用 goNextHint 记一次 recordHintStep) */}
              <div className="mb-2 2xl:mb-3 flex items-baseline gap-3 [@media(max-height:500px)]:mb-1! [@media(max-height:500px)]:gap-2!">
                <span className="font-normal text-[#006609] text-lg xl:text-xl 2xl:text-2xl [@media(max-height:500px)]:text-xs!">
                  {t('common.info')}{totalHints > 1 ? ` ${hintPage + 1}` : ''}
                </span>
                {hintPage < totalHints - 1 && (
                  <button
                    type="button"
                    onClick={goNextHint}
                    className="shrink-0 text-sm 2xl:text-[13px] text-[#006609] underline underline-offset-2 transition-opacity hover:opacity-70 [@media(max-height:500px)]:text-[10px]!"
                  >
                    {t('component.readmore')}
                  </button>
                )}
              </div>
              {/* 提示逐条呈现:用 embla 轮播承载,点 < > /「查看更多提示」使 hintPage 变化 → 上方 effect scrollTo 播放左右滑动过渡
                  (watchDrag:false → 只有点击切换的过渡动画,不启用手指拖拽手势) */}
              {/* 横屏:文案(flex-1) 与翻页箭头(shrink-0) 并排、items-end 底对齐 → 箭头贴文案最后一行右侧、超长文案不重叠;桌面/平板仍上下堆叠 */}
              <div className="[@media(max-height:500px)]:flex [@media(max-height:500px)]:items-end [@media(max-height:500px)]:gap-1.5">
                <Carousel
                  setApi={setTipsApi}
                  opts={{ align: 'start', watchDrag: false, loop: false }}
                  className="[@media(max-height:500px)]:flex-1! [@media(max-height:500px)]:min-w-0!"
                >
                  <CarouselContent className="items-start transition-[height] duration-200">
                    {aiTips.map((tip, i) => (
                      <CarouselItem key={i}>
                        <div className="text-[#006609] text-sm 2xl:text-[13px] leading-[1.55] [@media(max-height:500px)]:text-[11px]! [@media(max-height:500px)]:leading-[1.1]!">
                          <MarkdownRenderer>{tip}</MarkdownRenderer>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
                {/* 右下 < > 翻页(单条提示时不显示) */}
                {totalHints > 1 && (
                  <div className="mt-4 flex items-center justify-end gap-3 [@media(max-height:500px)]:mt-0! [@media(max-height:500px)]:shrink-0! [@media(max-height:500px)]:gap-1!">
                    <button
                      type="button"
                      aria-label="prev hint"
                      onClick={goPrevHint}
                      disabled={hintPage === 0}
                      className="flex size-[30px] items-center justify-center rounded-full bg-[#9CEA4F] text-black transition-opacity hover:opacity-90 disabled:opacity-30 [@media(max-height:500px)]:size-auto! [@media(max-height:500px)]:rounded-none! [@media(max-height:500px)]:bg-transparent! [@media(max-height:500px)]:text-[#006609]!"
                    >
                      <ChevronLeft className="size-3.5 [@media(max-height:500px)]:size-4!" />
                    </button>
                    <button
                      type="button"
                      aria-label="next hint"
                      onClick={goNextHint}
                      disabled={hintPage >= totalHints - 1}
                      className="flex size-[30px] items-center justify-center rounded-full bg-[#9CEA4F] text-black transition-opacity hover:opacity-90 disabled:opacity-30 [@media(max-height:500px)]:size-auto! [@media(max-height:500px)]:rounded-none! [@media(max-height:500px)]:bg-transparent! [@media(max-height:500px)]:text-[#006609]!"
                    >
                      <ChevronRight className="size-3.5 [@media(max-height:500px)]:size-4!" />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  // 提示/答案内联白卡:桌面/平板/手机横屏统一内联呈现(手机横屏走 tipsBlock 内的 [@media(max-height:500px)] 紧凑覆写)。
  // 甲方要求手机横屏「原题目+提示一起显示、不遮题」→ 由弹窗改回参照桌面的内联白卡(见 tipsBlock),交互(轮播/< >翻页/查看更多)与桌面同一套。
  const inlineTipsBlock = tipsBlock;

  // 是否在题目右上展示「答案狀態」(影响题目描述是否需要右内边距让出空间避免重叠)
  const showAnswerStatus = mode === 2 && currentAnswerState !== undefined && currentAnswerState !== AnswerState.WIP && currentAnswerState !== AnswerState.DRAFT;

  return (
    <>
      <LoadingOverlay isLoading={isLoading} text={t('common.submitting')} />
      <div className={`relative flex flex-col grow${fill ? " h-full min-h-0" : ""}`}>
        {showAnswerStatus && (
          <span
            className={`pointer-events-none absolute right-0 top-9 z-10 text-right text-sm font-medium leading-tight xl:top-10 xl:text-base 2xl:top-11 2xl:text-xl min-[1920px]:top-16! min-[1920px]:text-[32px]! [@media(max-height:500px)]:top-10! [@media(max-height:500px)]:right-0! [@media(max-height:500px)]:text-sm! ${
              currentAnswerState === AnswerState.CORRECT
                ? "text-chart-2"
                : currentAnswerState === AnswerState.PENDING_REVIEW
                  ? "text-yellow-600"
                  : currentAnswerState === AnswerState.WRONG
                    ? "text-[#FF0900]"
                    : ""
            }`}
          >
            {currentAnswerState === AnswerState.CORRECT
              ? (isMethodologyCorrect ? t('component.answerCorrect') : t('component.processerror'))
              : currentAnswerState === AnswerState.PENDING_REVIEW
                ? t('component.waitingreview')
                : currentAnswerState === AnswerState.WRONG
                  ? t('component.answerWrong')
                  : ""}
          </span>
        )}
        {/* ===== 题干:fill 时 shrink-0 固定置顶(不随答题区滚动);非 fill 用 display:contents 完全透明,DOM/间距不变 =====
             quiz-stem-fill:仅 fill 分支挂,供 global.css 放行只读公式 [part=content] 的 overflow 防顶部被裁(教师端非 fill 无此 class) */}
        <div className={fill ? "quiz-stem-fill flex flex-col shrink-0" : "contents"}>
        {currentIndex !== undefined && (
          <div className="text-base xl:text-lg 2xl:text-2xl min-[1920px]:text-[36px]! font-medium leading-tight mb-0.5 min-[1920px]:mb-3! text-[#1A1C1D] dark:text-gray-200 [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:mb-0.5! [@media(max-height:500px)]:leading-none!">
            {assignmentTitle && (
              <span className="text-[#64748B] dark:text-gray-400">
                {assignmentTitle} ·{" "}
              </span>
            )}
            {t('quiz.questionN', { defaultValue: '第 {{current}} / {{total}} 題', current: currentIndex + 1, total: totalItems ?? 1 })}
          </div>
        )}
        {mode === 2 && questionModel.isRewardCapped && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <Info className="size-4 shrink-0" />
            <span>{t('practice.rewardCapped')}</span>
          </div>
        )}
        <div className={`flex flex-col mb-1 text-sm xl:text-base 2xl:text-xl min-[1920px]:text-[32px]! leading-snug min-[1920px]:leading-normal! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:mb-0.5! [@media(max-height:500px)]:leading-tight! ${showAnswerStatus ? "pr-32 xl:pr-40 2xl:pr-52" : ""}`}>

          <ReadonlyQuestionDisplay
            questionText={isMultipleChoice && mcqData ? mcqData.stem : questionBody.text}
            className=""
          />
        </div>

        {/* Question images display using ImageUpload component
             小屏(<1920):题目图属固定题干(shrink-0),过高会挤扁下方 flex-1 答题区 →
             改按容器宽自适应 + 限高 ≤90px 回收纵向空间;1920 段 previewClassName/imgClassName 内各自恢复 h-32 原值冻结 */}
        {questionLocalImages && questionLocalImages.length > 0 && (
          <div className="mt-2 min-[1920px]:mt-4!">
            <ImageUpload
              disabled={true}
              initialImages={questionLocalImages}
              maxImages={8}
              imageType="question"
              isEditMode={true}
              previewClassName="h-auto max-h-[90px] max-w-full min-[1920px]:h-32! min-[1920px]:max-h-none! min-[1920px]:max-w-none!"
              imgClassName="h-auto max-h-[90px] max-w-full min-[1920px]:h-full! min-[1920px]:max-h-none! min-[1920px]:max-w-none!"
            />
          </div>
        )}
        </div>{/* ===== /题干 ===== */}

        {/* ===== 答题区:fill 时 flex-1 + overflow-y-auto 局部滚动,题干固定于上;非 fill 用 display:contents 透明 ===== */}
        <div ref={answerScrollRef} className={fill ? "ml-thin-scroll flex flex-col min-h-0 flex-1 overflow-y-auto" : "contents"}>
        <div className="mt-0.5 min-[1920px]:mt-2! flex flex-col text-sm text-chart-1 [@media(max-height:500px)]:mt-0!">
          {/* 教师评语单独成行(答案状态已上提为 absolute,见组件顶部) */}
          {questionModel.question?.teacherReview && (
            <span className="block text-right text-base leading-tight text-[#1A1C1D]">
              {t('component.teacherremark') + questionModel.question.teacherReview}
            </span>
          )}
          {subQuestionResults.length > 1 && (
            <div className="mt-1 text-sm">
              {subQuestionResults.map((result, index) => (
                <span key={`subq-${index}`} className={`mr-3 ${result.isCorrect ? 'text-chart-2' : 'text-chart-1'}`}>
                  {t('common.subquestion')}{index + 1}: {result.isCorrect ? t('component.correct') : t('component.wrong')}
                </span>
              ))}
            </div>
          )}
          {!showTipsBelowDesc && inlineTipsBlock}
        </div>

        {/* 答题区域 */}
        {isMultipleChoice && mcqData && (
          <div className="mt-4">
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 [@media(max-height:500px)]:grid-cols-2! [@media(max-height:500px)]:gap-x-[13px]! [@media(max-height:500px)]:gap-y-[9px]!">
              {mcqData.options.map((option) => (
                /* 选项卡: 内边距/圆角/间距/字号整套随断点等比缩(平板别太高太空), 1920 锚原值逐像素不变; items-center 居中对齐字母与公式 */
                <div key={option.label} className={`flex cursor-pointer items-center gap-2 rounded-[14px] p-3.5 text-[22px] font-[450] transition-colors xl:gap-2.5 xl:rounded-[16px] xl:p-4 xl:text-[24px] 2xl:gap-3 2xl:rounded-[20px] 2xl:p-5 2xl:text-[26px] min-[1920px]:rounded-[25px]! min-[1920px]:p-6! min-[1920px]:text-[32px]! [@media(max-height:500px)]:h-[37px]! [@media(max-height:500px)]:p-2! [@media(max-height:500px)]:gap-1.5! [@media(max-height:500px)]:rounded-[11px]! [@media(max-height:500px)]:text-xs! ${mcqAnswers.indexOf(option.label) !== -1 ? "bg-[#9CEA4E]" : "bg-white hover:bg-black/[0.02]"}`} onClick={(e) => {
                  if (readonly || isAnsweredCorrectly) {
                    e.preventDefault();
                    return;
                  }

                  // Only one choice in early stage for UE. MCQ have been changed to FRQ.
                  // let index = mcqAnswers.indexOf(option.label);
                  // if (index !== -1) {
                  //   mcqAnswers.splice(index, 1);
                  // } else {
                  //   mcqAnswers.push(option.label);
                  // }
                  mcqAnswers = [option.label];
                  setMCQAnswers(Array.from(mcqAnswers));
                  setAnswerBody({
                    text: mcqAnswers.sort().join(''),
                    backendImageFile: [],
                    localImages: [],
                  });
                }}>
                  {/* 标签字母去掉固定 text-lg, 跟随父级字号同步缩放, 保留加粗 */}
                  <span className="font-semibold text-[#1A1C1D]">{option.label}.</span>
                  <div className="flex-1 relative">
                    {/* 统一渲染管线(多行公式修复)+ dev-cpp 响应式字号(写到 math-field host, 公式不继承父级 font-size) */}
                    <ReadonlyQuestionDisplay questionText={option.content} fieldClassName="text-[22px] xl:text-[24px] 2xl:text-[26px] min-[1920px]:text-[32px]! [@media(max-height:500px)]:text-xs!" />
                    <div style={{
                      // 关键：绝对定位覆盖在内容节点上
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,

                      // 尺寸与内容节点完全一致（继承容器尺寸）
                      width: '100%',
                      height: '100%',

                      zIndex: 10 // 确保在内容节点上方
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!isMultipleChoice || !mcqData) && (
          <div className="mt-0.5 min-[1920px]:mt-2! [@media(max-height:500px)]:mt-0!">
            {!readonly && !isAnsweredCorrectly ?
              <>
                {/* 开放题作答提示：引导用户使用键盘或手写板作答 */}
                <div className="mb-3 rounded-xl bg-muted/50 px-4 py-2 text-sm text-muted-foreground xl:mb-4 [@media(max-height:500px)]:mb-1! [@media(max-height:500px)]:px-2.5! [@media(max-height:500px)]:py-1! [@media(max-height:500px)]:text-xs!">
                  {t('quiz.openEndedHint', { defaultValue: '請在下方輸入你的答案，或切換到手寫板書寫。' })}
                </div>
                {inlineTipsBlock && (
                  <div className="mb-3 xl:mb-4 2xl:mb-5 [@media(max-height:500px)]:mb-0.5!">{inlineTipsBlock}</div>
                )}
                <div className="flex items-start mb-1.5 min-[1920px]:mb-4! [@media(max-height:500px)]:mb-1!">
                  <Tabs defaultValue={currentInputMethod} value={currentInputMethod} onValueChange={setCurrentInputMethod} className="relative w-full gap-0">
                    <TabsList className={"relative z-10 h-auto gap-1 border-0 bg-transparent p-0 [@media(max-height:500px)]:gap-0!"}>
                      <TabsTrigger className="cursor-pointer h-10 w-auto min-w-[140px] rounded-t-[16px] rounded-b-none xl:rounded-t-[20px] 2xl:rounded-t-[25px] bg-transparent px-6 text-xs font-light text-[#1A1C1D]/60 shadow-none after:hidden before:hidden hover:bg-black/[0.04] xl:h-12 xl:min-w-[180px] xl:text-sm 2xl:h-11 min-[1920px]:h-[58px]! 2xl:min-w-[240px] 2xl:text-base min-[1920px]:text-xl! data-[state=active]:bg-white data-[state=active]:text-xs data-[state=active]:font-medium data-[state=active]:text-[#1A1C1D] data-[state=active]:shadow-none data-[state=active]:hover:bg-white xl:data-[state=active]:text-sm 2xl:data-[state=active]:text-lg min-[1920px]:data-[state=active]:text-2xl! [@media(max-height:500px)]:h-[30px]! [@media(max-height:500px)]:min-w-0! [@media(max-height:500px)]:w-[112px]! [@media(max-height:500px)]:px-2! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:data-[state=active]:text-xs!" key={"keyboard"} value={"keyboard"}>{t('component.keyboard')}</TabsTrigger>
                      <TabsTrigger className="cursor-pointer h-10 w-auto min-w-[120px] rounded-t-[16px] rounded-b-none xl:rounded-t-[20px] 2xl:rounded-t-[25px] bg-transparent px-6 text-xs font-light text-[#1A1C1D]/60 shadow-none after:hidden before:hidden hover:bg-black/[0.04] xl:h-12 xl:min-w-[160px] xl:text-sm 2xl:h-11 min-[1920px]:h-[58px]! 2xl:min-w-[220px] 2xl:text-base min-[1920px]:text-xl! data-[state=active]:bg-white data-[state=active]:text-xs data-[state=active]:font-medium data-[state=active]:text-[#1A1C1D] data-[state=active]:shadow-none data-[state=active]:hover:bg-white xl:data-[state=active]:text-sm 2xl:data-[state=active]:text-lg min-[1920px]:data-[state=active]:text-2xl! [@media(max-height:500px)]:h-[30px]! [@media(max-height:500px)]:min-w-0! [@media(max-height:500px)]:w-[112px]! [@media(max-height:500px)]:px-2! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:data-[state=active]:text-xs!" key={`${questionModel.questionInfo.id}-handwrite`} value={"handwrite"}>{t('component.handwrite')}</TabsTrigger>
                      <TabsTrigger className="cursor-pointer h-10 w-auto min-w-[120px] rounded-t-[16px] rounded-b-none xl:rounded-t-[20px] 2xl:rounded-t-[25px] bg-transparent px-6 text-xs font-light text-[#1A1C1D]/60 shadow-none after:hidden before:hidden hover:bg-black/[0.04] xl:h-12 xl:min-w-[160px] xl:text-sm 2xl:h-11 min-[1920px]:h-[58px]! 2xl:min-w-[220px] 2xl:text-base min-[1920px]:text-xl! data-[state=active]:bg-white data-[state=active]:text-xs data-[state=active]:font-medium data-[state=active]:text-[#1A1C1D] data-[state=active]:shadow-none data-[state=active]:hover:bg-white xl:data-[state=active]:text-sm 2xl:data-[state=active]:text-lg min-[1920px]:data-[state=active]:text-2xl! [@media(max-height:500px)]:h-[30px]! [@media(max-height:500px)]:min-w-0! [@media(max-height:500px)]:w-[112px]! [@media(max-height:500px)]:px-2! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:data-[state=active]:text-xs!" key={"image"} value={"image"}>{t('component.uploadimg')}</TabsTrigger>
                    </TabsList>
                    <div className={`flex flex-col rounded-[25px] bg-white p-2.5 min-[1920px]:p-[14px]! [@media(max-height:500px)]:p-1.5! ${
                      currentInputMethod === "keyboard" ? "rounded-tl-none" : ""
                    }`}>
                    {/* EN-14 输入法说明: 按译文自带换行拆三条,14px/#4B5563(白底 7.6:1 过 AA),文案不改 */}
                    <ol className="block max-w-full list-decimal pl-7 pt-0.5 mb-1 text-sm leading-relaxed text-[#4B5563] xl:mb-1.5 min-[1920px]:mb-3! 2xl:text-[15px] [@media(max-height:500px)]:pl-4! [@media(max-height:500px)]:pt-0! [@media(max-height:500px)]:mb-1.5! [@media(max-height:500px)]:text-[10px]! [@media(max-height:500px)]:leading-tight!">
                      {t('component.inputmethodtips')
                        .split('\n')
                        .map((line) => line.replace(/^\s*\(?\d+\)?\*?\s*/, '').trim())
                        .filter(Boolean)
                        .map((line, i) => (
                          <li key={i} className="mb-0.5 last:mb-0">{line}</li>
                        ))}
                    </ol>
                    <div className={`flex flex-col rounded-[15px] border-2 border-dashed p-3 lg:p-4 2xl:p-2.5 min-[1920px]:p-4! [@media(max-height:500px)]:p-1.5! ${
                      currentInputMethod === "keyboard" ||
                      currentInputMethod === "image" ||
                      currentInputMethod === "handwrite"
                        ? "border-[#E2E2E2]"
                        : "border-transparent"
                    } ${
                      /* 手机横屏 + 键盘输入: 复刻 Figma 569:1901 虚线框布局 = 2×2 网格
                         [上傳輔助圖 + 左·跨两行][math 右上(⌨≡ 在其右上角)][保存/提交 右下]。
                         列 auto|1fr、行 1fr|auto。回收纵向堆叠高度,整屏落入 277px 面板。
                         仅限该模式,桌面/平板/手写/上传态不受影响。 */
                      currentInputMethod === "keyboard"
                        ? "[@media(max-height:500px)]:grid! [@media(max-height:500px)]:[grid-template-columns:auto_1fr]! [@media(max-height:500px)]:[grid-template-rows:minmax(0,1fr)]! [@media(max-height:500px)]:items-stretch! [@media(max-height:500px)]:gap-x-2!"
                        : ""
                    }`}>
                    <TabsContent key={"keyboard"} value={"keyboard"} className="[@media(max-height:500px)]:flex-1! [@media(max-height:500px)]:min-w-0! [@media(max-height:500px)]:col-start-2! [@media(max-height:500px)]:row-start-1!">
                      {/* EN-15：math-field 补键盘焦点环（rounded 只作用于焦点态描边，静态尺寸不变） */}
                      <math-field className={`flex-1 w-full h-[96px] rounded-lg focus-within:ring-2 focus-within:ring-[#1A1C1D] focus-within:ring-offset-2 [@media(min-height:1000px)]:h-[260px] [@media(max-height:500px)]:h-[40px]!`}
                        defaultValue={questionModel.question!.answer}
                        onInput={evt => onMathInput(evt.target.value)}
                        ref={answerRef}
                        aria-label={t('component.mathAnswerLabel', { defaultValue: '第 {{index}} 題答案輸入區', index: (currentIndex ?? 0) + 1 })}
                      >
                        {wrapDisplaylines(answerBody.text)}
                      </math-field>
                    </TabsContent>
                    <TabsContent key={`${questionModel.questionInfo.id}-handwrite`} value={"handwrite"} forceMount className="data-[state=inactive]:hidden">
                      {/* 手写板区 - 无label，宽度与文字区输入框一致 */}
                      <div className="flex items-start">
                        <div className="flex-1">
                          <AnswerCanvas
                            key={questionModel.questionInfo.id}
                            ref={childRef}
                            canvasId={"answer-canvas-" + questionModel.questionInfo.id!}
                            onOcrRecognize={handleOcrRecognize}
                            isOcrProcessing={isOcrProcessing}
                          />
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent key={"image"} value={"image"} forceMount className="data-[state=inactive]:hidden">
                      <div className="flex flex-col items-start gap-2 min-h-[150px] [@media(min-height:1000px)]:min-h-[260px] [@media(max-height:500px)]:min-h-0!">
                        {/* EN-15：加号块补可见文字标签 + 焦点环(input 透明故用 focus-within 显示在 label)；块尺寸不变，文字在手机横屏 49×49 档隐藏 */}
                        <label className="relative flex h-[120px] w-[180px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[15px] bg-[#D9D9D9] transition hover:brightness-95 focus-within:ring-2 focus-within:ring-[#1A1C1D] focus-within:ring-offset-2 xl:h-[140px] xl:w-[200px] 2xl:h-[150px] 2xl:w-[220px] [@media(max-height:500px)]:h-[49px]! [@media(max-height:500px)]:w-[49px]! [@media(max-height:500px)]:gap-0! [@media(max-height:500px)]:rounded-[4px]!">
                          <Plus className="size-8 text-white 2xl:size-10" strokeWidth={2.5} />
                          <span className="text-sm font-medium leading-none text-white 2xl:text-base [@media(max-height:500px)]:hidden">
                            {t('component.addImage', { defaultValue: '新增圖片' })}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="absolute inset-0 cursor-pointer opacity-0"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleFile(f);
                              e.target.value = ''; // 允许重复选同一文件
                            }}
                          />
                        </label>
                        <span className="text-[13px] font-light leading-5 text-[#939393] 2xl:text-[15px]">
                          {t('component.uploadhandwriting')}
                        </span>
                      </div>
                    </TabsContent>

                    <div className={`mt-4 flex items-end justify-between gap-4 2xl:mt-3 min-[1920px]:mt-6! [@media(max-height:500px)]:mt-2! ${
                      /* 键盘输入横向(Figma 569:1901): 本行框自身 display:contents 解散,
                         让 [上傳輔助圖 +] 与 [保存/提交] 直接成为上方虚线框 grid 的子项,
                         各自落到左列(跨两行)与右下角。桌面/平板/手写/上传态仍为普通 flex 行。 */
                      currentInputMethod === "keyboard"
                        ? "[@media(max-height:500px)]:[display:contents]!"
                        : ""
                    }`}>
                      <div className={`flex-1 min-w-0 ${currentInputMethod === "keyboard" ? "[@media(max-height:500px)]:flex-none! [@media(max-height:500px)]:col-start-1! [@media(max-height:500px)]:row-start-1! [@media(max-height:500px)]:self-start!" : ""}`}>
                        <ImageUpload
                          variant="figma"
                          onImagesChange={handleImageUploadChange}
                          maxImages={8}
                          disabled={false}
                          initialImages={answerBody.localImages}
                          imageType="answer"
                        />
                      </div>
                      {!readonly && (
                        <div className={`flex shrink-0 items-center gap-3 ${currentInputMethod === "keyboard" ? "[@media(max-height:500px)]:col-start-2! [@media(max-height:500px)]:row-start-1! [@media(max-height:500px)]:justify-self-end! [@media(max-height:500px)]:self-end! [@media(max-height:500px)]:gap-1.5!" : ""}`}>
                          {mode === 1 ? (
                            <>
                              <Button onClick={() => { onSave(); }} disabled={isAnsweredCorrectly} className="h-[44px] rounded-[15px] bg-[#9CEA4E] px-5 text-sm font-medium text-black shadow-none hover:bg-[#8fe03d] xl:h-12 xl:px-6 xl:text-base 2xl:h-[53px] 2xl:px-7 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                                {t('component.save')}
                              </Button>
                              {isLastQuestion && (
                                <Button onClick={() => { if (onSubmitHomework) { onSubmitHomework(); } }} className="h-[44px] rounded-[15px] bg-[#FFD57B] px-5 text-sm font-medium text-black shadow-none hover:bg-[#f5c560] xl:h-12 xl:px-6 xl:text-base 2xl:h-[53px] 2xl:px-7 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                                  {t('component.submithomework')}
                                </Button>
                              )}
                            </>
                          ) : (
                            currentAnswerState !== AnswerState.PENDING_REVIEW ?
                              <Button ref={submitBtnRef} onClick={() => { onSubmit(); }} disabled={isAnsweredCorrectly || isLoading} className="h-[44px] rounded-[15px] bg-[var(--primary)] px-5 text-sm font-medium text-[var(--primary-foreground)] shadow-none hover:brightness-95 disabled:opacity-60 xl:h-12 xl:px-6 xl:text-base 2xl:h-[53px] 2xl:px-7 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                                {isLoading ? t('common.submitting') : t('component.submit')}
                              </Button>
                              : <></>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                    </div>
                  </Tabs>
                </div>
              </>
              :
              <>
                {/* 只读文字区 - label 横排置上方:旧版 vertical-rl 会让英文 "Type Here" 逐字母竖排 */}
                <div className="mb-4">
                  <div className="text-base font-medium text-gray-700 mb-2">
                    {t("common.textarea", { defaultValue: "文字區" })}
                  </div>
                  <math-field className={`w-full ${isCorrect ? "border-gray-200" : "border-red-500"} border-2 rounded-md`}
                    defaultValue={questionModel.question!.answer}
                    onInput={evt => onMathInput(evt.target.value)}
                    read-only
                  >
                    {answerBody.text}
                  </math-field>
                </div>
                {
                  handWriting &&
                  <div>
                    <img title={t('component.handwriting')} src={handWriting} style={handWritingStyle} onClick={() => {
                      if (handWritingStyle) {
                        setHandWritingStyle(undefined);
                      } else {
                        setHandWritingStyle({ width: 100, height: 100 });
                      }
                    }}>
                    </img>
                  </div>
                }

                {answerBody.localImages && answerBody.localImages.length > 0 && (
                  <>
                    {/* 只读模式下的画图区 - label 横排置于组件上方（兼容中英文） */}
                    <div className="mt-4">
                      <div className="text-base font-medium text-gray-700 mb-2">
                        {t("common.imagearea", { defaultValue: "畫圖區" })}
                      </div>
                      <ImageUpload
                        maxImages={8}
                        disabled={true}
                        initialImages={answerBody.localImages}
                        imageType="answer"
                        isEditMode={true}
                      />
                    </div>
                  </>
                )}
              </>
            }
          </div>
        )}

        {/* AI批改结果显示 - 在答案区域后面，与题目答案同级显示 */}
        {readonly && questionModel.question?.aiReview && (
          <div className="mt-6">
            <div className="text-base font-medium text-[#006609] mb-3">
              {t('component.aiReview')}
            </div>
            {/* dev-cpp 外层 Figma 圆角白底容器 + github/dev 的卡片化 AI 复盘渲染 */}
            <div className="rounded-[25px] border border-[#E6E7EB] bg-white p-5">
              <AiReviewDisplay aiReview={questionModel.question.aiReview} />
            </div>
          </div>
        )}

        {!readonly && isMultipleChoice && mcqData && (
          <div className="flex justify-end mt-6 gap-3 items-center [@media(max-height:500px)]:mt-3!">
            {mode === 1 ? (
              <>
                <Button onClick={() => {
                  onSave();
                }} disabled={isAnsweredCorrectly} className="h-[52px] rounded-[15px] bg-[#9CEA4E] px-7 text-sm font-medium text-black shadow-none hover:bg-[#8fe03d] xl:text-base 2xl:h-[53px] 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                  {t('component.save')}
                </Button>
                {isLastQuestion && (
                  <Button onClick={() => {
                    if (onSubmitHomework) {
                      onSubmitHomework();
                    }
                  }} className="h-[52px] rounded-[15px] bg-[#FFD57B] px-7 text-sm font-medium text-black shadow-none hover:bg-[#f5c560] xl:text-base 2xl:h-[53px] 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                    {t('component.submithomework')}
                  </Button>
                )}
              </>
            ) : (
              currentAnswerState !== AnswerState.PENDING_REVIEW ?
                <Button ref={submitBtnRef} onClick={() => {
                  onSubmit();
                }} disabled={isAnsweredCorrectly || isLoading} className="h-[52px] rounded-[15px] bg-[var(--primary)] px-7 text-sm font-medium text-[var(--primary-foreground)] shadow-none hover:brightness-95 disabled:opacity-60 xl:text-base 2xl:h-[53px] 2xl:text-xl 2xl:font-medium min-[1920px]:text-2xl! [@media(max-height:500px)]:h-[23px]! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:rounded-md!">
                  {isLoading ? t('common.submitting') : t('component.submit')}
                </Button>
                : <></>
            )}
          </div>
        )}

        {/* 老师修正面板 - 只在只读模式下且用户是老师时且有AI批改结果时显示 */}
        {readonly && getCurrentUser()?.role === UserInfoResponseRole.TEACHER && questionModel.question?.aiReview && (
          <div className="mt-6 p-4 border rounded-lg bg-gray-50">
            <h3 className="text-base font-medium text-gray-700 mb-4">
              {t('component.teacherGrading')}
            </h3>

            {/* 正确/错误单选框 */}
            <div className="mb-4">
              <RadioGroup
                value={teacherIsCorrect ? "correct" : "incorrect"}
                onValueChange={(value) => setTeacherIsCorrect(value === "correct")}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="correct" id="correct" />
                  <Label htmlFor="correct" className="cursor-pointer">
                    {t('component.correct')}
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="incorrect" id="incorrect" />
                  <Label htmlFor="incorrect" className="cursor-pointer">
                    {t('component.wrong')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* 评语输入 */}
            <div className="mb-4">
              <Label htmlFor="teacherComment" className="text-base font-medium">
                {t('component.comment')}
              </Label>
              <Input
                id="teacherComment"
                value={teacherComment}
                onChange={(e) => setTeacherComment(e.target.value)}
                placeholder={t('component.commentPlaceholder')}
                className="mt-2"
              />
            </div>

            {/* 提交按钮 - 居中显示 */}
            <div className="flex justify-center">
              <Button
                onClick={onTeacherGradingSubmit}
                disabled={isGradingSubmitting}
                className="min-w-[100px]"
              >
                {isGradingSubmitting
                  ? t('common.submitting')
                  : t('component.submitGrading')
                }
              </Button>
            </div>
          </div>
        )}
        </div>{/* ===== /答题区 ===== */}
      </div>


      {!readonly && mode !== 1 &&
        <div className="absolute right-0 top-2 z-10 flex flex-row items-center gap-4 xl:top-2.5 xl:gap-5 2xl:top-3 2xl:gap-6 [@media(max-height:500px)]:top-2! [@media(max-height:500px)]:gap-4!">
          {
            isSubmitted && !isCorrect && (dueDate ? compareDesc(new Date(), dueDate) === -1 : true) &&
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <KeyRound className="size-5 cursor-pointer text-[#2C2C2C] transition-opacity hover:opacity-60 xl:size-6 2xl:size-[25px] [@media(max-height:500px)]:size-5!" onClick={() => {
                      if (localStorage.getItem('hideShowAnswerWarning') === 'true') {
                        void onAIRemark(removeDisplaylines(answerBody.text), true);
                      } else {
                        setDontShowAnswerWarning(false);
                        setShowAnswerConfirmOpen(true);
                      }
                    }}></KeyRound>
                  </TooltipTrigger>
                  <TooltipContent className={BUBBLE_TOOLTIP_CLASS} arrowClassName={BUBBLE_TOOLTIP_ARROW_CLASS}>
                    <p>{t('component.showanswer')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <AlertDialog open={showAnswerConfirmOpen} onOpenChange={setShowAnswerConfirmOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('component.showAnswerConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('component.showAnswerConfirmDesc')}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="flex items-center gap-2">
                    <Checkbox id="dontShowAgain" checked={dontShowAnswerWarning} onCheckedChange={(checked) => setDontShowAnswerWarning(checked === true)} />
                    <Label htmlFor="dontShowAgain" className="text-sm">{t('component.dontShowAgain')}</Label>
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('component.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      if (dontShowAnswerWarning) {
                        localStorage.setItem('hideShowAnswerWarning', 'true');
                      }
                      void onAIRemark(removeDisplaylines(answerBody.text), true);
                    }}>{t('component.confirm')}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          }

          {
            isSubmitted && questionModel.question?.teacherId && <StudentMessageDialog studentMessage={studentMessage ?? {
              title: "",
              description: "",
              replyType: 0,
              type: 0,
              teacherMessage: undefined,
              studentId: getCurrentUser()!.id,
              teacherId: questionModel.question!.teacherId,
              questionInfoId: questionModel.questionInfo.id,
              questionId: questionModel.question!.id,
            }} />
          }

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <BotMessageSquare className="size-5 cursor-pointer text-[#2C2C2C] transition-opacity hover:opacity-60 xl:size-6 2xl:size-[25px] [@media(max-height:500px)]:size-5!" onClick={() => {
                  navigate(ROUTE.STUDENT.AI_BOT, {
                    state: {
                      questionModel: questionModel,
                      assignmentId: assignmentId,
                      tabId: homeworkState?.tabId,
                      itemId: homeworkState?.itemId,
                    }
                  });
                }}></BotMessageSquare>
              </TooltipTrigger>
              <TooltipContent className={BUBBLE_TOOLTIP_CLASS} arrowClassName={BUBBLE_TOOLTIP_ARROW_CLASS}>
                <p>{t('component.askai')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {
            isSubmitted && !isCorrect &&
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Lightbulb className="size-5 cursor-pointer text-[#2C2C2C] transition-opacity hover:opacity-60 xl:size-6 2xl:size-[25px] [@media(max-height:500px)]:size-5!" onClick={async () => { await onAITips(getCurrentUser()!.id, questionModel.question!.id!); }}></Lightbulb>
                </TooltipTrigger>
                <TooltipContent className={BUBBLE_TOOLTIP_CLASS} arrowClassName={BUBBLE_TOOLTIP_ARROW_CLASS}>
                  <p>{t('component.showtips')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        </div>
      }
    </>

  )
}

const QuizItemForwarded = forwardRef(QuizItem);

export interface QuizInfo {
  title?: string,
  language?: string,
}

export function QuizCarousel({ quizProps, dueDate, readonly, onFinish, onBeforeSubmit, shouldShowExpectedDialog, onExpectedDialogShown, fillHeight }: {
  quizProps: GetQuestionListResponse & QuizInfo,
  dueDate?: Date,
  readonly: boolean,
  onFinish?: () => void,
  onBeforeSubmit?: () => Promise<boolean>,
  shouldShowExpectedDialog?: boolean,
  onExpectedDialogShown?: () => void,
  /**
   * 视口自适应答题布局(仅作业/随心练桌面树传入)。开启后在宽 ≥ 768(含 1920+)区间:
   * 题干固定置顶、答题区局部滚动、题号栏钉在灰卡底部。<768(移动竖屏/横屏另有布局)保持原「整卡滚动」行为。
   * 实际生效由下方 isNarrowFill(matchMedia ≥768)与该开关共同决定 → narrowFill。
   * (原先上限 1919 把 1920×1080 屏排除在外 → 题号栏掉出一屏;现放开上限,让钉底覆盖 ≥1920,1920 尺寸/间距仍冻结。)
   */
  fillHeight?: boolean,
}) {
  const { getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addKp, addGs, kp, gs } = useGamification();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  // 视口自适应答题布局的生效开关:仅当外部 fillHeight 且视口宽 ≥768(平板/笔电/桌面,含 1920+)时启用。
  // <768(移动竖屏/横屏另有布局)为 false → 走原「整卡滚动、embla 动态高度」。
  // (1920 尺寸/间距仍冻结;这里只把「题号栏钉底」的溢出行为也覆盖到 ≥1920,原上限 1919 会漏掉 1920 屏。)
  const [isNarrowFill, setIsNarrowFill] = useState(false);
  const isNarrowFillRef = useRef(false);
  const narrowFill = !!fillHeight && isNarrowFill;
  const [currentIndex, setCurrentIndex] = useState(HOMEWORK_STATE.itemId || 0);
  const [totalItems, setTotalItems] = useState(0);
  const [showIncompleteDialog, setShowIncompleteDialog] = useState(false);
  const [incompleteQuestions, setIncompleteQuestions] = useState<number[]>([]);
  const [isSubmittingHomework, setIsSubmittingHomework] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [submissionStats, setSubmissionStats] = useState<any>(null);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState<number>(0);
  const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);
  const quizItemRefs = useRef<(QuizItemHandle | null)[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const prevIndexRef = useRef<number | null>(null);
  // Expected correctness rating state
  const [showExpectedCorrectnessDialog, setShowExpectedCorrectnessDialog] = useState(false);
  const [expectedCorrectCount, setExpectedCorrectCount] = useState<number | null>(null);

  // 监听 shouldShowExpectedDialog，当 END pulse 完成后显示预期表现对话框
  useEffect(() => {
    if (shouldShowExpectedDialog) {
      setShowExpectedCorrectnessDialog(true);
      if (onExpectedDialogShown) {
        onExpectedDialogShown();
      }
    }
  }, [shouldShowExpectedDialog, onExpectedDialogShown]);

  // Helper function to determine if it's homework mode
  // Homework mode: mode=1 or assignmentStat=null
  // Practice mode: mode=2
  const isHomeworkMode = () => {
    return quizProps.assignmentStat?.mode === 1 || quizProps.assignmentStat === null || quizProps.assignmentStat === undefined;
  };

  const getAnswerState = async (index: number): Promise<AnswerState> => {
    const question = quizProps.questionModelList[index].question!;

    // 作业模式：只有WIP和DRAFT两种状态，不会出现isCompleted=true
    if (isHomeworkMode()) {
      if (question.draft === "1") {
        return AnswerState.DRAFT;
      }
      return AnswerState.WIP;
    }

    // 做题模式：有CORRECT、WRONG和PENDING_REVIEW三种状态
    if (!question.isCompleted) {
      // isCompleted=false说明需要人工批改，检查replyType判断是否已批改
      try {
        // 检查缓存
        const cached = studentMessageCacheRef.current.get(question.id!);
        if (cached) {
          if (cached.replyType === 0) {
            return AnswerState.PENDING_REVIEW;
          } else if (cached.replyType === 1) {
            if (question.isCorrectAnswer) {
              return AnswerState.CORRECT;
            } else {
              return AnswerState.WRONG;
            }
          }
        }

        // 如果缓存中没有，则调用API
        const rsp = await getStudentMessage({ questionId: question.id, type: 1 });
        if (rsp.code === 200 && rsp.data && rsp.data.studentMessages && rsp.data.studentMessages.length > 0) {
          const studentMessage = rsp.data.studentMessages[0];
          const replyType = studentMessage.replyType;
          console.log(`Question ${question.id} replyType:`, replyType);

          // 缓存结果
          studentMessageCacheRef.current.set(question.id!, { replyType });

          // replyType=0：未批改 → PENDING_REVIEW
          // replyType=1：已批改 → 根据isCorrectAnswer判断
          if (replyType === 0) {
            return AnswerState.PENDING_REVIEW;
          } else if (replyType === 1) {
            // 已批改，根据isCorrectAnswer判断是CORRECT还是WRONG
            if (question.isCorrectAnswer) {
              return AnswerState.CORRECT;
            } else {
              return AnswerState.WRONG;
            }
          }
        }
        // 如果没有StudentMessage，默认为WIP
        return AnswerState.WIP;
      } catch (error) {
        console.error("Error fetching student message:", error);
        // 出错时，默认为WIP
        return AnswerState.WIP;
      }
    } else {
      // isCompleted=true，根据isCorrectAnswer判断是CORRECT还是WRONG
      if (question.isCorrectAnswer) {
        return AnswerState.CORRECT;
      } else {
        return AnswerState.WRONG;
      }
    }
  };

  const [answerStates, setAnswerStates] = useState<AnswerState[]>((new Array(quizProps ? quizProps.questionModelList.length : 0).fill(AnswerState.WIP)));

  // 缓存StudentMessage结果，避免重复请求
  const studentMessageCacheRef = useRef<Map<number, { replyType?: number }>>(new Map());

  // 异步初始化答案状态
  useEffect(() => {
    const initializeAnswerStates = async () => {
      const states: AnswerState[] = [];
      for (let i = 0; i < quizProps.questionModelList.length; i++) {
        const state = await getAnswerState(i);
        states.push(state);
      }
      setAnswerStates(states);
    };

    initializeAnswerStates();
  }, [quizProps.questionModelList]);

  // 检查未作答题目 - 检查本地图片而不是filesJson
  const checkIncompleteQuestions = (): number[] => {
    const incomplete: number[] = [];
    quizProps.questionModelList.forEach((questionModel, index) => {
      const question = questionModel.question;
      if (question) {
        const answerText = question.answer?.trim() || "";
        // 检查本地图片（通过ref获取）
        const quizItemRef = quizItemRefs.current[index];
        const localImages = quizItemRef?.getLocalImages() || [];
        const hasImages = localImages.length > 0;
        // 如果答案文本和本地图片都为空，则认为未作答
        if (!answerText && !hasImages) {
          incomplete.push(index + 1); // 题号从1开始
        }
      }
    });
    return incomplete;
  };



  // 提交作业 - 依次调用每个 QuizItem 的 onSubmit，然后提交作业
  const onSubmitHomework = async () => {
    try {
      // 检查是否有未作答题目
      const incomplete = checkIncompleteQuestions();
      if (incomplete.length > 0) {
        setIncompleteQuestions(incomplete);
        setShowIncompleteDialog(true);
        return;
      }

      setShowPreviewDialog(true);
    } catch (error) {
      console.error("Submit homework error", error);
      setShowSubmissionDialog(false);
      toast.error(t('common.error'));
    } finally {
      setIsSubmittingHomework(false);
    }
  };

  useEffect(() => {
    installMathFieldWrapper();
  }, []);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = () => {
      const newIndex = carouselApi.selectedScrollSnap();
      const prevIndex = prevIndexRef.current;

      // Fire answerEnd for previous question when navigating away
      if (prevIndex !== null && prevIndex !== newIndex) {
        const prevQuestion = quizProps.questionModelList[prevIndex]?.question;
        if (prevQuestion?.id && !prevQuestion.isCompleted) {
          console.log("[Response Time] Fire Answer End for question", prevQuestion.id); //TODO: remove in production
          answerEnd({ questionId: prevQuestion.id }).catch(console.error);
        }
      }

      // Fire answerStart for new question
      const newQuestion = quizProps.questionModelList[newIndex]?.question;
      if (newQuestion?.id && !newQuestion.isCompleted) {
        console.log("[Response Time] Fire Answer Start for question", newQuestion.id); //TODO: remove in production
        answerStart({ questionId: newQuestion.id }).catch(console.error);
      }

      prevIndexRef.current = newIndex;

      HOMEWORK_STATE.itemId = newIndex;
      setCurrentIndex(HOMEWORK_STATE.itemId);
      setTotalItems(carouselApi.scrollSnapList().length);
    };

    updateCarouselState();

    carouselApi.on("select", updateCarouselState);

    return () => {
      // Fire answerEnd for current question on unmount (e.g., switching assignments)
      const currentIndex = prevIndexRef.current;
      if (currentIndex !== null) {
        const currentQuestion = quizProps.questionModelList[currentIndex]?.question;
        if (currentQuestion?.id && !currentQuestion.isCompleted) {
          console.log("[Response Time] Fire Answer End on unmount for question", currentQuestion.id); //TODO: remove in production
          answerEnd({ questionId: currentQuestion.id }).catch(console.error);
        }
      }
      carouselApi.off("select", updateCarouselState);
    };
  }, [carouselApi]);

  // Fire answerEnd on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentIndex = prevIndexRef.current;
      if (currentIndex !== null) {
        const currentQuestion = quizProps.questionModelList[currentIndex]?.question;
        if (currentQuestion?.id && !currentQuestion.isCompleted) {
          console.log("[Response Time] Fire Answer End on beforeunload for question", currentQuestion.id); //TODO: remove in production
          // Use sendBeacon for reliable delivery during page unload
          navigator.sendBeacon(`/api/assignment/answerEnd?questionId=${currentQuestion.id}`, '');
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [quizProps.questionModelList]);

  const highestInView = useCallback((): number | null => {
    if (!carouselApi) return null;

    const {
      options: { axis },
      slideRects
    } = carouselApi.internalEngine()

    if (axis === 'y') return null;

    let slideHeights = slideRects.map((slideRect) => slideRect.height)
    const { slideRegistry } = carouselApi.internalEngine()
    const selectedIndexes = slideRegistry[carouselApi.selectedScrollSnap()]

    if (!selectedIndexes) return null

    const ret = selectedIndexes
      .map((index) => slideHeights[index])
      .reduce((a, b) => Math.max(a, b), 0)
    return ret;
  }, [carouselApi]);

  const setContainerHeight = useCallback((newHeight?: number): void => {
    // narrowFill 模式下容器改用 CSS h-full 撑满视口(题干固定/答题区滚动),不再按内容强制像素高度。
    if (isNarrowFillRef.current) return;
    const height = Math.max(newHeight ?? 0, highestInView() ?? 0)
    if (height === 0 || !carouselApi) return

    carouselApi.containerNode().style.height = `${height}px`
  }, [carouselApi, highestInView]);

  // 跟踪视口宽是否 ≥768:进入该区间(且 fillHeight)则清掉之前 embla 设的内联高度(交给 CSS h-full);
  // 离开(<768)则重新按内容量高。仅在 fillHeight 时挂监听。
  useEffect(() => {
    if (!fillHeight) {
      isNarrowFillRef.current = false;
      setIsNarrowFill(false);
      return;
    }
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => {
      isNarrowFillRef.current = mq.matches;
      setIsNarrowFill(mq.matches);
      if (mq.matches) {
        const container = carouselApi?.containerNode();
        if (container) container.style.height = '';
      } else {
        setContainerHeight();
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [fillHeight, carouselApi, setContainerHeight]);

  // Debounced version to break resize feedback loops
  const heightTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const setContainerHeightDebounced = useCallback((newHeight?: number) => {
    clearTimeout(heightTimerRef.current);
    heightTimerRef.current = setTimeout(() => setContainerHeight(newHeight), 50);
  }, [setContainerHeight]);

  useEffect(() => {
    if (!carouselApi) return;

    heightEvents.forEach((evt) => carouselApi.on(evt, () => setContainerHeightDebounced()))
    setContainerHeight()

    return () => {
      heightEvents.forEach((evt) => carouselApi.off(evt, () => setContainerHeightDebounced()))
      clearTimeout(heightTimerRef.current);
      const container = carouselApi.containerNode()
      container.style.height = ''
      if (!container.getAttribute('style')) container.removeAttribute('style')
    };
  }, [carouselApi, setContainerHeight, setContainerHeightDebounced]);

  const scrollToIndex = (index: number) => {
    carouselApi?.scrollTo(index);
  };

  const onSlideSizeChanged: OnResizeCallback = useCallback((payload) => {
    if (!carouselApi) return;
    if (!payload.entry) return;
    let selectedId = payload.entry.target.id.split('-')[1];
    if (carouselApi.selectedScrollSnap() != Number.parseInt(selectedId)) return;

    if (payload.width !== null && payload.height !== null) {
      setContainerHeightDebounced(payload.height);
    }
  }, [setContainerHeightDebounced, carouselApi]);

  return (
    // narrowFill:整棵子树改为纵向 flex 撑满灰卡 → 上段 Carousel(flex-1 滚动) + 下段题号栏(shrink-0 钉底)。
    <div className={`relative w-full${narrowFill ? " flex min-h-0 flex-1 flex-col" : ""}`}>
      <Carousel
        setApi={setCarouselApi}
        opts={{
          loop: true,
          dragFree: false,
          watchDrag: false,
          startIndex: currentIndex,
        }}
        className={narrowFill ? "flex min-h-0 flex-1 flex-col" : "w-full"}
      >
        <CarouselContent
          viewportClassName={narrowFill ? "min-h-0 flex-1" : undefined}
          className={narrowFill ? "h-full" : undefined}
        >
          {quizProps &&
            quizProps.questionModelList.map((questionModel, index) => (
              <CarouselItem
                key={index}
                id={`${quizProps.assignmentId + "-" + index}`}
                className={narrowFill ? "h-full" : "h-fit"}
                onSizeChanged={onSlideSizeChanged}
              >
                <div className={`relative w-full${narrowFill ? " h-full" : ""}`}>
                  <QuizItemForwarded
                    ref={(ref) => {
                      quizItemRefs.current[index] = ref;
                    }}
                    key={index}
                    readonly={readonly}
                    dueDate={dueDate}
                    language={quizProps.language}
                    questionModel={questionModel}
                    fill={narrowFill}
                    mode={isHomeworkMode() ? 1 : 2}
                    isLastQuestion={
                      index === quizProps.questionModelList.length - 1
                    }
                    assignmentId={quizProps.assignmentId}
                    assignmentTitle={quizProps.title}
                    currentIndex={index}
                    totalItems={totalItems}
                    onNavigateNext={() => scrollToIndex(index + 1)}
                    onSubmitHomework={onSubmitHomework}
                    onStateChanged={(state) => {
                      const newStates = Array.from(answerStates);
                      newStates[index] = state;
                      setAnswerStates(newStates);
                      if (
                        newStates.every(
                          (state) => state === AnswerState.CORRECT,
                        ) &&
                        onFinish
                      ) {
                        onFinish();
                      }
                    }}
                  ></QuizItemForwarded>
                </div>
              </CarouselItem>
            ))}
        </CarouselContent>
      </Carousel>

      {totalItems > 1 && (
        // Figma 1920 设计稿:分页器顶部距离答题模块 30px;小屏沿用之前 24px(pt-6).
        // 手机横屏:不再 sticky 浮在底部,随答题内容正常排到末尾(用户反馈底部浮动条很奇怪)
        // narrowFill(平板/笔电):shrink-0 作为灰卡最底一行常驻钉底(布局流,非 fixed 浮动),上方答题区滚动。
        <div className={`right-0 left-0 flex place-content-center items-center gap-3 pt-3 2xl:pt-4 min-[1920px]:pt-[30px]! [@media(max-height:500px)]:gap-1! [@media(max-height:500px)]:pt-2!${narrowFill ? " shrink-0" : ""}`}>
          <button
            onClick={() => scrollToIndex(currentIndex - 1)}
            aria-label="previous"
            className="pointer-events-auto flex h-9 w-9 min-[1920px]:h-[42px]! min-[1920px]:w-[42px]! items-center justify-center rounded-[10px] bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95 [@media(max-height:500px)]:h-6! [@media(max-height:500px)]:w-6! [@media(max-height:500px)]:rounded-[4px]!"
          >
            <ChevronLeft className="size-5 [@media(max-height:500px)]:size-3.5!" />
          </button>
          {Array.from({ length: totalItems }).map((_, index) => {
            const st = answerStates[index];
            const isCurrent = currentIndex === index;
            // 中性态（未作答/草稿/待批改）用设计稿灰，当前页用品牌绿；
            // 已批改的对/错保留功能性配色（设计稿静态图未体现，但属功能反馈，不可丢）
            const isNeutral =
              st === AnswerState.WIP ||
              st === AnswerState.DRAFT ||
              st === AnswerState.PENDING_REVIEW;
            // 题号 aria-label 的状态名 key（仅供屏幕阅读器；视觉图例/tooltip 已按原系统去掉）
            const statusKey =
              st === AnswerState.CORRECT
                ? 'quiz.legendAnswered'
                : st === AnswerState.WRONG
                  ? 'quiz.legendWrong'
                  : st === AnswerState.PENDING_REVIEW
                    ? 'quiz.legendPendingReview'
                    : st === AnswerState.DRAFT
                      ? 'quiz.draft'
                      : isCurrent
                        ? 'quiz.legendCurrent'
                        : 'quiz.legendUnanswered';
            return (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => scrollToIndex(index)}
                      aria-current={isCurrent ? 'true' : undefined}
                      aria-label={t('quiz.questionStatus', {
                        defaultValue: '第 {{index}} 題：{{status}}',
                        index: index + 1,
                        status: t(statusKey, { defaultValue: '' }),
                      })}
                      className="flex h-9 w-9 min-[1920px]:h-[42px]! min-[1920px]:w-[42px]! place-self-center items-center justify-center rounded-full text-lg font-medium transition-colors [@media(max-height:500px)]:h-6! [@media(max-height:500px)]:w-6! [@media(max-height:500px)]:text-xs!"
                      style={{
                        backgroundColor: !isNeutral
                          ? st === AnswerState.CORRECT
                            ? "var(--color-chart-2)"
                            : "var(--color-chart-1)"
                          : isCurrent
                            ? "var(--practice-dialog-accent)"
                            : "#E6E7EB",
                        color: !isNeutral
                          ? "#fff"
                          : isCurrent
                            ? "var(--practice-dialog-accent-fg)"
                            : "#8F8F8F",
                        border:
                          st === AnswerState.DRAFT ||
                          st === AnswerState.PENDING_REVIEW
                            ? "2px solid var(--color-chart-2)"
                            : undefined,
                      }}
                    >
                      {index + 1}
                    </button>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
            );
          })}
          <button
            onClick={() => scrollToIndex(currentIndex + 1)}
            aria-label="next"
            className="pointer-events-auto flex h-9 w-9 min-[1920px]:h-[42px]! min-[1920px]:w-[42px]! items-center justify-center rounded-[10px] bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95 [@media(max-height:500px)]:h-6! [@media(max-height:500px)]:w-6! [@media(max-height:500px)]:rounded-[4px]!"
          >
            <ChevronRight className="size-5 [@media(max-height:500px)]:size-3.5!" />
          </button>
        </div>
      )}

      {/* Preview Dialog */}
      <HomeworkPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        questions={quizProps.questionModelList.map((qm) => qm.questionInfo)}
        answers={quizProps.questionModelList.map((qm) => qm.question!)}
        mode={PreviewMode.HOMEWORK}
        title={quizProps.title || t("common.homework")}
        name={getCurrentUser()!.name}
        onConfirm={async () => {
          setShowPreviewDialog(false);
          // Reset expected correct count to null
          setExpectedCorrectCount(null);

          // 调用父组件的回调来检查 END pulse
          // 如果返回 true，表示需要显示 END pulse，不显示预期表现对话框
          // 如果返回 false，表示不需要 END pulse，直接显示预期表现对话框
          let shouldWaitForEndPulse = false;
          if (onBeforeSubmit) {
            shouldWaitForEndPulse = await onBeforeSubmit();
          }

          // 只有在不需要等待 END pulse 时，才显示预期表现对话框
          if (!shouldWaitForEndPulse) {
            setShowExpectedCorrectnessDialog(true);
          }
        }}
        isLoading={false}
        language={quizProps.language}
        confirmClassName="bg-[#FFD57B] text-black hover:bg-[#f5c560]"
      />


      {/* Expected Correctness Rating Dialog */}
      <Dialog
        open={showExpectedCorrectnessDialog}
        onOpenChange={setShowExpectedCorrectnessDialog}
      >
        <DialogContent
          className="sm:max-w-[600px] !p-0 !gap-0 border-none shadow-2xl bg-white dark:bg-gray-900 rounded-[40px] overflow-hidden [@media(max-height:500px)]:!max-w-[400px] [@media(max-height:500px)]:!rounded-[20px]"
          showTitle={false}
        >
          <div className="px-8 pb-8 pt-10 lg:px-10 [@media(max-height:500px)]:!p-6">
            {/* Question 1: Expected Correctness */}
            <div className="mb-8 [@media(max-height:500px)]:!mb-3">
              <h3 className="text-[24px] font-semibold text-[#1A1C1D] dark:text-gray-200 mb-4 text-center leading-snug [@media(max-height:500px)]:!text-[18px] [@media(max-height:500px)]:!text-left [@media(max-height:500px)]:!mb-2">
                {t("selfAssess.q1Title")}
              </h3>
              <p className="text-base text-[#1A1C1D]/70 dark:text-gray-400 mb-6 text-center [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!text-left [@media(max-height:500px)]:!mb-2">
                {t("selfAssess.q1Desc")}
              </p>
              <div
                className="py-4 [@media(max-height:500px)]:!py-2"
                onPointerDown={() =>
                  expectedCorrectCount === null && setExpectedCorrectCount(0)
                }
              >
                <Slider
                  value={[expectedCorrectCount ?? 0]}
                  onValueChange={(val) => setExpectedCorrectCount(val[0])}
                  min={0}
                  max={quizProps.questionModelList.length}
                  step={1}
                  className="w-full [&_[data-slot=slider-track]]:h-3 [&_[data-slot=slider-track]]:bg-[#F3F3F4] [&_[data-slot=slider-range]]:bg-[#9CEA4F] [&_[role=slider]]:size-6 [&_[role=slider]]:bg-white [&_[role=slider]]:border-2 [&_[role=slider]]:border-[#9CEA4F] [&_[role=slider]]:shadow-md [&_[role=slider]]:transition-transform [&_[role=slider]]:hover:scale-110"
                />
                <div className="mt-4 flex justify-between text-base font-semibold text-gray-600 dark:text-gray-400 [@media(max-height:500px)]:!hidden">
                  <span>0</span>
                  <span>{quizProps.questionModelList.length}</span>
                </div>
                {expectedCorrectCount === null ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 py-4 text-center [@media(max-height:500px)]:!hidden"
                  >
                    <p className="text-base text-gray-400 dark:text-gray-500">
                      {t("selfAssess.scaleNotSelected")}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key={expectedCorrectCount}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="mt-6 rounded-[20px] bg-[#9CEA4F]/15 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-[#9CEA4F]/50 dark:border-green-700 p-4 shadow-md [@media(max-height:500px)]:!hidden"
                  >
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-3xl font-bold text-[#5FA800] dark:text-green-400">
                        {expectedCorrectCount}
                      </span>
                      <span className="text-2xl text-gray-400 dark:text-gray-500">/</span>
                      <span className="text-2xl font-semibold text-gray-600 dark:text-gray-400">
                        {quizProps.questionModelList.length}
                      </span>
                    </div>
                    <p className="text-base font-semibold text-[#5FA800] dark:text-green-300">
                      {Math.round(
                        (expectedCorrectCount /
                          quizProps.questionModelList.length) *
                          100,
                      )}% confidence
                    </p>
                  </motion.div>
                )}
                {/* 手机横屏:用 q1Format 摘要行替代上面的 min/max 行 + 信心盒(对齐 Figma 570-250) */}
                <div className="hidden [@media(max-height:500px)]:block mt-2 text-center text-[13px] font-semibold text-[#1A1C1D] dark:text-gray-200">
                  {expectedCorrectCount === null
                    ? t("selfAssess.scaleNotSelected")
                    : t("selfAssess.q1Format", {
                        x: expectedCorrectCount,
                        y: quizProps.questionModelList.length,
                        percent: Math.round(
                          (expectedCorrectCount /
                            quizProps.questionModelList.length) *
                            100,
                        ),
                      })}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex gap-3 [@media(max-height:500px)]:!gap-2">
              <Button
                variant="outline"
                onClick={() => setShowExpectedCorrectnessDialog(false)}
                className="h-14 flex-1 min-w-0 whitespace-normal leading-tight rounded-[20px] border-2 border-[#E6E7EB] bg-white text-base font-medium text-[#939393] hover:bg-gray-50 hover:text-[#939393] [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl"
              >
                {t("common.cancel")}
              </Button>
              <Button
                disabled={expectedCorrectCount === null}
                onClick={async () => {
                setShowExpectedCorrectnessDialog(false);
                try {
                  setIsSubmittingHomework(true);
                  setShowSubmissionDialog(true);
                  setSubmissionProgress(0);
                  setIsSubmissionComplete(false);
                  toast.dismiss();

                  if (!quizProps.assignmentId) {
                    toast.error(t("common.error"));
                    return;
                  }

                  const totalQuestions = quizProps.questionModelList.length;

                  // Save expected correctness to self-assessment data before submitting answers
                  const expectedCorrectnessRatio =
                    (expectedCorrectCount ?? 0) / totalQuestions;
                  try {
                    await setSelfAssessmentResult({
                      assignmentId: quizProps.assignmentId,
                      studentId: getCurrentUser()!.id,
                      resultJson: JSON.stringify({
                        expected_correctness: expectedCorrectnessRatio,
                      }),
                    });
                    console.log(
                      "Saved expected correctness:",
                      expectedCorrectnessRatio,
                    );
                  } catch (error) {
                    console.warn("Failed to save expected correctness:", error);
                  }

                  // 依次调用每个 QuizItem 的 onSubmit 函数，累积奖励
                  const allPointAwards: {
                    currencyType?: string;
                    amount?: number;
                    eventType?: string;
                    description?: string;
                  }[] = [];
                  for (let i = 0; i < totalQuestions; i++) {
                    const quizItemRef = quizItemRefs.current[i];
                    if (quizItemRef) {
                      try {
                        const awards = await quizItemRef.onSubmit(true);
                        if (awards) allPointAwards.push(...awards);
                        // 更新进度
                        setSubmissionProgress(
                          Math.round(((i + 1) / totalQuestions) * 100),
                        );
                      } catch (error) {
                        console.error("Error submitting question", i, error);
                        setShowSubmissionDialog(false);
                        toast.error(t("common.error"));
                        return;
                      }
                    }
                  }

                  // 所有题目答案提交成功后，获取作业状态
                  const rsp = await getAssignmentStat({
                    studentId: getCurrentUser()!.id,
                    assignmentId: quizProps.assignmentId,
                  });

                  if (rsp.code === 200 && rsp.data) {
                    console.log("Submit homework successful", rsp.data);
                    setSubmissionStats(rsp.data);

                    // Fetch fresh question data to get accurate duration
                    try {
                      const questionRsp = await getQuestionList({
                        studentId: getCurrentUser()!.id,
                        assignmentId: quizProps.assignmentId!,
                        newest: true,
                      });
                      if (
                        questionRsp.code === 200 &&
                        questionRsp.data?.questionModelList
                      ) {
                        const totalSeconds =
                          questionRsp.data.questionModelList.reduce(
                            (sum, q) =>
                              sum + (q.question?.answerDurationInSecond || 0),
                            0,
                          );
                        setTotalDurationSeconds(totalSeconds);
                        console.log("Total duration:", totalSeconds, "seconds");
                      }
                    } catch (error) {
                      console.warn("Failed to fetch question duration:", error);
                    }

                    // Play combined reward animation
                    let totalKpAwarded = 0;
                    let totalGsAwarded = 0;
                    for (const award of allPointAwards) {
                      if (award.currencyType === "KP" && award.amount)
                        totalKpAwarded += award.amount;
                      else if (award.currencyType === "GS" && award.amount)
                        totalGsAwarded += award.amount;
                    }
                    if (totalKpAwarded > 0 || totalGsAwarded > 0) {
                      const hasBoth = totalKpAwarded > 0 && totalGsAwarded > 0;
                      const spread = hasBoth ? 20 + Math.random() * 20 : 0;
                      if (totalKpAwarded > 0) {
                        void flyKpToHeader({
                          delta: totalKpAwarded,
                          nextValue: kp + totalKpAwarded,
                          combined: hasBoth,
                          xOffset: -spread,
                        }).then(() => {
                          addKp(totalKpAwarded);
                        });
                      }
                      if (totalGsAwarded > 0) {
                        void flyGsToHeader({
                          delta: totalGsAwarded,
                          nextValue: gs + totalGsAwarded,
                          combined: hasBoth,
                          xOffset: spread,
                        }).then(() => {
                          addGs(totalGsAwarded);
                        });
                      }
                    }

                    setIsSubmissionComplete(true);
                  } else {
                    console.error(
                      "Submit homework failed",
                      rsp.code,
                      rsp.message,
                    );
                    setShowSubmissionDialog(false);
                    toast.error(t("common.apierror") + rsp.message);
                  }
                } catch (error) {
                  console.error("Submit homework error", error);
                  setShowSubmissionDialog(false);
                  toast.error(t("common.error"));
                } finally {
                  setIsSubmittingHomework(false);
                }
              }}
              className={
                expectedCorrectCount === null
                  ? "h-14 flex-1 min-w-0 whitespace-normal leading-tight cursor-not-allowed rounded-[20px] bg-[var(--practice-dialog-accent)] text-base font-semibold text-[var(--practice-dialog-accent-fg)] opacity-50 [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl [@media(max-height:500px)]:!flex-[2]"
                  : "h-14 flex-1 min-w-0 whitespace-normal leading-tight cursor-pointer rounded-[20px] bg-[var(--practice-dialog-accent)] text-base font-semibold text-[var(--practice-dialog-accent-fg)] hover:brightness-95 [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl [@media(max-height:500px)]:!flex-[2]"
              }
            >
              {t("component.submithomework")}
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 未作答题目提示弹窗 */}
      <Dialog
        open={showIncompleteDialog}
        onOpenChange={setShowIncompleteDialog}
      >
        <DialogContent
          className="sm:max-w-[600px] !p-0 !gap-0 border-none shadow-2xl bg-white dark:bg-gray-900 rounded-[40px] overflow-hidden [@media(max-height:500px)]:!max-w-[400px] [@media(max-height:500px)]:!rounded-[20px]"
          showTitle={false}
        >
          <div className="px-8 pb-8 pt-10 lg:px-10 [@media(max-height:500px)]:!p-6">
            <h3 className="mb-4 text-[24px] font-semibold text-[#1A1C1D] dark:text-gray-200 [@media(max-height:500px)]:!text-[18px] [@media(max-height:500px)]:!mb-2">
              {t("component.incompleteQuestionsTitle")}
            </h3>
            <p className="mb-4 text-base text-[#1A1C1D]/70 dark:text-gray-400 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!mb-1">
              {t("component.incompleteQuestionsDesc")}
            </p>
            <div className="mb-6 text-base text-[#1A1C1D] dark:text-gray-300 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!mb-4">
              {t("component.pleaseCompleteQuestions")}
              <span className={`font-semibold text-red-500 ${i18n.language === "en" ? "ml-1" : "mx-1"}`}>
                {i18n.language === "en"
                  ? incompleteQuestions.length > 2
                    ? `${incompleteQuestions.slice(0, -1).join(", ")}, and ${incompleteQuestions[incompleteQuestions.length - 1]}`
                    : incompleteQuestions.join(" and ")
                  : incompleteQuestions.join("、")}
              </span>
              {t("component.questions")}
            </div>
            <div className="flex gap-3 [@media(max-height:500px)]:!gap-2">
              <Button
                variant="outline"
                onClick={() => setShowIncompleteDialog(false)}
                className="h-14 flex-1 min-w-0 whitespace-normal leading-tight rounded-[20px] border-2 border-[#E6E7EB] bg-white text-base font-medium text-[#939393] hover:bg-gray-50 hover:text-[#939393] [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  setShowIncompleteDialog(false);
                  // 导航到第一个未作答题目
                  if (incompleteQuestions.length > 0) {
                    const firstIncompleteIndex = incompleteQuestions[0] - 1;
                    scrollToIndex(firstIncompleteIndex);
                  }
                }}
                className="h-14 flex-[2] min-w-0 whitespace-normal leading-tight rounded-[20px] bg-[var(--practice-dialog-accent)] text-base font-semibold text-[var(--practice-dialog-accent-fg)] hover:brightness-95 [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl"
              >
                {t("component.backAndCompleteQuestions")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 提交进度和完成合并弹窗 */}
      <Dialog
        open={showSubmissionDialog}
        onOpenChange={setShowSubmissionDialog}
      >
        <DialogContent
          className="sm:max-w-[600px] !p-0 !gap-0 border-none shadow-2xl bg-white dark:bg-gray-900 rounded-[40px] overflow-hidden [@media(max-height:500px)]:!max-w-[400px] [@media(max-height:500px)]:!rounded-[20px]"
          showTitle={false}
          onPointerDownOutside={(e) =>
            !isSubmissionComplete && e.preventDefault()
          }
        >
          <div className="px-8 pb-8 pt-10 lg:px-10 [@media(max-height:500px)]:!p-6">
            <h3 className="mb-6 text-[24px] font-semibold text-[#1A1C1D] dark:text-gray-200 [@media(max-height:500px)]:!text-[18px] [@media(max-height:500px)]:!mb-3">
              {isSubmissionComplete
                ? t("component.submissionComplete")
                : t("component.submittingHomework")}
            </h3>
            <div className="grid gap-4 [@media(max-height:500px)]:!gap-2 [@media(max-height:500px)]:[&_span]:!text-[13px]">
              {!isSubmissionComplete ? (
                <>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-[#F3F3F4]">
                    <div
                      className="h-3 rounded-full bg-[var(--practice-dialog-accent)] transition-all duration-300"
                      style={{
                        width: `${submissionProgress}%`,
                        boxShadow: "0 0 12px rgba(0,77,96,0.3)",
                      }}
                    ></div>
                  </div>
                  <div className="text-right text-base font-medium text-[#1A1C1D] dark:text-gray-300 [@media(max-height:500px)]:!text-center [@media(max-height:500px)]:!text-[13px]">
                    {submissionProgress}%
                  </div>
                </>
              ) : (
                <>
                  {submissionStats && submissionStats.assignmentStat && (
                    <>
                      <div className="flex items-center justify-between border-b border-[#E6E7EB] py-3 [@media(max-height:500px)]:!py-2">
                        <span className="text-base text-[#1A1C1D] dark:text-gray-400">
                          {t("component.totalQuestions")}
                        </span>
                        <span className="text-base font-semibold text-[#1A1C1D] dark:text-gray-200">
                          {submissionStats.assignmentStat.totalQuestionNum}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-[#E6E7EB] py-3 [@media(max-height:500px)]:!py-2">
                        <span className="text-base text-[#1A1C1D] dark:text-gray-400">
                          {t("component.correctQuestions")}
                        </span>
                        <span className="text-base font-semibold text-[#1A1C1D] dark:text-gray-200">
                          {submissionStats.assignmentStat.correctQuestionNum}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-[#E6E7EB] py-3 [@media(max-height:500px)]:!py-2">
                        <span className="text-base text-[#1A1C1D] dark:text-gray-400">
                          {submissionStats.assignmentStat.reviewQuestionNum === 0
                            ? t("component.correctPercentage")
                            : t("component.pendingReview")}
                        </span>
                        <span className="text-base font-semibold text-[#1A1C1D] dark:text-gray-200">
                          {submissionStats.assignmentStat.reviewQuestionNum === 0
                            ? `${Math.round((submissionStats.assignmentStat.correctQuestionNum / submissionStats.assignmentStat.totalQuestionNum) * 100)}%`
                            : submissionStats.assignmentStat.reviewQuestionNum}
                        </span>
                      </div>
                      {/* Total completion time */}
                      <div className="flex items-center justify-between py-3 [@media(max-height:500px)]:!py-2">
                        <span className="text-base text-[#1A1C1D] dark:text-gray-400">
                          {t("component.timeTaken")}
                        </span>
                        <span className="text-base font-semibold text-[#1A1C1D] dark:text-gray-200">
                          {(() => {
                            const minutes = Math.floor(totalDurationSeconds / 60);
                            const remainingSeconds = totalDurationSeconds % 60;
                            if (minutes > 0) {
                              return `${minutes} ${t("selfAssess.minutes")} ${remainingSeconds} ${t("selfAssess.seconds")}`;
                            }
                            return `${remainingSeconds} ${t("selfAssess.seconds")}`;
                          })()}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            {isSubmissionComplete && (
              <div className="mt-6 flex gap-3 [@media(max-height:500px)]:!mt-3 [@media(max-height:500px)]:!gap-2">
                <Button
                  onClick={() => {
                    setShowSubmissionDialog(false);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }}
                  className="h-14 flex-1 min-w-0 whitespace-normal leading-tight rounded-[20px] bg-[#FFD57B] text-base font-semibold text-black hover:bg-[#FFD57B]/90 [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl"
                >
                  {t("component.confirm")}
                </Button>
                <Button
                  onClick={() => {
                    setShowSubmissionDialog(false);
                    navigate(ROUTE.STUDENT.SELF_ASSESS, {
                      state: { preSelectedAssignmentId: quizProps.assignmentId },
                    });
                  }}
                  className="h-14 flex-1 min-w-0 whitespace-normal leading-tight rounded-[20px] bg-[#9CEA4F] text-base font-semibold text-black hover:bg-[#9CEA4F]/90 [@media(max-height:500px)]:!h-11 [@media(max-height:500px)]:!text-[13px] [@media(max-height:500px)]:!rounded-xl [@media(max-height:500px)]:!flex-[2]"
                >
                  {t("selfAssess.selfAssess")}
                  <ArrowRight className="ml-1 h-4 w-4 shrink-0" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}